import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Grid,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Fab,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Slider,
  LinearProgress
} from '@mui/material';
import CopyableTag from '../Shared/CopyableTag';
import {
  ArrowBack,
  Add,
  Delete,
  Visibility,
  Edit,
  Save,
  Cancel,
  Inventory,
  QrCodeScanner,
  Info,
  Store,
  CalendarToday,
  Person,
  AttachMoney,
  Upload,
  FileUpload,
  LocalShipping,
  CheckCircle,
  ContentCopy
} from '@mui/icons-material';
import { useAppData } from '../../context/AppDataContext';
import { FEATURE_PERMISSIONS } from '../../utils/frontendPermissions';
import {
  getOrderDetail,
  createOrder,
  updateOrder,
  lookupSKU,
  lookupManySKU,
  deleteOrder,
  updateOrderPurchases,
  processImportOrder,
  getAllActiveTags,
  getAllTagInSystem,
  getTransferDetail,
  getTransferByCode,
  createTransfer,
  updateTransfer,
  confirmTransfer,
  completeTransfer,
  cancelTransfer,
  deleteTransfer,
  getUserList,
  getSimpleUserList
} from '../../services/api';
import { useFlashMessage } from '../../hooks/useFlashMessage';
import { useDeviceSelection } from '../../hooks/useDeviceSelection';
import { useMQTT } from '../../hooks/useMQTT';
import { getSelectedRfidDevice, saveSelectedRfidDevice } from '../../utils/rfidDeviceUtils';

const getProductImageSrc = (product) => {
  if (!product) return '';
  return product.productImage || product.image_url || product.image || '';
};

// Product Detail Modal Component for viewing/editing individual products
const ProductDetailModal = ({
  open,
  onClose,
  product,
  onUpdateProduct,
  allOrderSerials = [],
  orderStatus = 'pending',
  orderId = null,
  allProducts = [],
  onRefreshData,
  activeTags = [],
  tagToSKU = {},
  isSerialEditingLocked = false
}) => {
  const { showError, showSuccess } = useFlashMessage();
  const { selectedDeviceObjects, getDeviceName } = useDeviceSelection();
  const { subscribe, unsubscribe, isConnected, subscribedTopics } = useMQTT();
  const { frontendPermissions, permissions } = useAppData();

  const isAdmin = permissions.includes('admin');
  const canEditProductQuantity =
    isAdmin ||
    frontendPermissions.includes(
      FEATURE_PERMISSIONS.INVENTORY_TRANSFER.EDIT_PRODUCTS
    );
  const canEditSerialsPermission =
    isAdmin ||
    frontendPermissions.includes(
      FEATURE_PERMISSIONS.INVENTORY_TRANSFER.EDIT_SERIALS
    );
  const canSaveProduct = canEditProductQuantity || canEditSerialsPermission;

  const [formData, setFormData] = useState({
    SKU: '',
    name: '',
    id: '',
    qty: 1,
    serials: [],
    serialsConfirm: [],
    rfidDevice: ''
  });
  const [newSerial, setNewSerial] = useState('');
  const [skipSerial, setSkipSerial] = useState([]);
  const [inputRef, setInputRef] = useState(null);
  const [currentDeviceTopic, setCurrentDeviceTopic] = useState(null);

  // Functions to manage skipSerial array
  const addToSkipSerial = (serial) => {
    if (!skipSerial.includes(serial)) {
      setSkipSerial(prev => [...prev, serial]);
    }
  };

  const removeFromSkipSerial = (serial) => {
    setSkipSerial(prev => prev.filter(s => s !== serial));
  };

  const clearSkipSerial = () => {
    setSkipSerial([]);
  };

  // Get RFID devices from selected devices in localStorage
  const rfidDevices = selectedDeviceObjects;

  useEffect(() => {
    if (product) {
      // Load RFID device from localStorage using utility function
      const savedRfidDevice = getSelectedRfidDevice('transfer');

      setFormData({
        SKU: product.SKU || '',
        name: product.name || '',
        id: product.id || product._id || '',
        qty: product.qty || 1,
        serials: product.serials || [],
        serialsConfirm: product.serialsConfirm || [],
        rfidDevice: savedRfidDevice
      });
    }
  }, [product]);

  // Subscribe to MQTT topic when modal opens and device is selected
  useEffect(() => {
    if (open && formData.rfidDevice && isConnected) {
      const deviceTopic = formData.rfidDevice;
      subscribe(deviceTopic, (topic, message) => {
        // Message received from device
      });
      setCurrentDeviceTopic(deviceTopic);
    }

    // Cleanup: unsubscribe when modal closes
    return () => {
      if (currentDeviceTopic) {
        unsubscribe(currentDeviceTopic);
        setCurrentDeviceTopic(null);
      }
    };
  }, [open, formData.rfidDevice, isConnected, subscribe, unsubscribe, currentDeviceTopic]);

  // Listen to MQTT messages via custom event
  useEffect(() => {
    if (!open) return;

    // Function to handle MQTT messages
    const handleMqttMessage = (event) => {
      const { topic, message, packet, timestamp } = event.detail;
      // Only process messages from the current selected device
      if (topic === currentDeviceTopic) {
        let scannedSerial = '';
        // Handle RFID scan result
        if (typeof message === 'string') {
          // If message is a string, treat it as a serial number
          scannedSerial = message.trim();
        } else if (typeof message === 'object' && message.serial) {
          // If message is an object with serial property
          scannedSerial = message.serial.trim();
        }

        if (isSerialEditingLocked) {
          return;
        }

        // Process the scanned serial with validation
        if (scannedSerial) {
          // Check if serial already exists in current product
          if (formData.serials.includes(scannedSerial)) {
            return;
          }

          // Check if serial already exists in any other product in the order
          if (allOrderSerials.includes(scannedSerial)) {
            return;
          }

          // Check if serial is in skip list
          if (skipSerial.includes(scannedSerial)) {
            return;
          }

          // Check if serial is in active tags (skip list from API)
          if (activeTags.includes(scannedSerial)) {
            // return;
          }

          // Check if tag exists in tagToSKU mapping and if its SKU matches the product's SKU
          if (Object.keys(tagToSKU).length > 0) {
            const tagSKU = tagToSKU[scannedSerial];
            if (!tagSKU) {
              // Tag is not in the system mapping - filter it out
              return;
            }
            if (tagSKU !== formData.SKU) {
              // Tag's SKU doesn't match the product's SKU - filter it out
              return;
            }
          }

          // Add the valid serial
          setFormData(prev => ({
            ...prev,
            serials: [...prev.serials, scannedSerial]
          }));
        }
      }
    };

    // Add event listener
    window.addEventListener('mqttMessage', handleMqttMessage);

    // Cleanup: remove event listener when modal closes
    return () => {
      window.removeEventListener('mqttMessage', handleMqttMessage);
    };
  }, [open, currentDeviceTopic, formData.serials, formData.SKU, allOrderSerials, skipSerial, activeTags, tagToSKU, isSerialEditingLocked]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Handle RFID device selection
    if (field === 'rfidDevice') {
      // Save RFID device selection using utility function
      saveSelectedRfidDevice(value, 'transfer');

      // Unsubscribe from previous device topic if exists
      if (currentDeviceTopic) {
        unsubscribe(currentDeviceTopic);
      }

      // Subscribe to new device topic if device is selected and MQTT is connected
      if (value && isConnected) {
        const deviceTopic = value; // Use device ID as topic
        subscribe(deviceTopic, (topic, message) => {
          // Message received from device
        });

        setCurrentDeviceTopic(deviceTopic);
      } else {
        setCurrentDeviceTopic(null);
      }
    }
  };

  const handleAddSerial = () => {
    if (isSerialEditingLocked) {
      showError('Transfer has been confirmed. Serial numbers are locked.');
      return;
    }

    const trimmedSerial = newSerial.trim();

    if (!trimmedSerial) return;

    // Check if serial already exists in current product
    if (formData.serials.includes(trimmedSerial)) {
      setNewSerial('');
      // Refocus input after clearing
      setTimeout(() => {
        if (inputRef) inputRef.focus();
      }, 0);
      return;
    }

    // Check if serial already exists in any other product in the order
    if (allOrderSerials.includes(trimmedSerial)) {
      setNewSerial('');
      // Refocus input after clearing
      setTimeout(() => {
        if (inputRef) inputRef.focus();
      }, 0);
      return;
    }

    // Check if serial is in skip list
    if (skipSerial.includes(trimmedSerial)) {
      // If serial is in skip list, just clear the input
      setNewSerial('');
      // Refocus input after clearing
      setTimeout(() => {
        if (inputRef) inputRef.focus();
      }, 0);
      return;
    }

    // Check if serial is in active tags (skip list from API)
    if (activeTags.includes(trimmedSerial)) {
      // If serial is in active tags, just clear the input
      setNewSerial('');
      // Refocus input after clearing
      setTimeout(() => {
        if (inputRef) inputRef.focus();
      }, 0);
      return;
    }

    // Check if tag exists in tagToSKU mapping and if its SKU matches the product's SKU
    if (Object.keys(tagToSKU).length > 0) {
      const tagSKU = tagToSKU[trimmedSerial];
      if (!tagSKU) {
        // Tag is not in the sensor mapping - filter it out
        setNewSerial('');
        setTimeout(() => {
          if (inputRef) inputRef.focus();
        }, 0);
        return;
      }
      if (tagSKU !== formData.SKU) {
        // Tag's SKU doesn't match the product's SKU - filter it out
        setNewSerial('');
        setTimeout(() => {
          if (inputRef) inputRef.focus();
        }, 0);
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      serials: [...prev.serials, trimmedSerial]
    }));
    setNewSerial('');
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddSerial();
    }
  };

  const handleRemoveSerial = (serialToRemove) => {
    setFormData(prev => ({
      ...prev,
      serials: prev.serials.filter(serial => serial !== serialToRemove)
    }));
  };

  const handleSave = async () => {
    try {
      // First update the local product
      if (onUpdateProduct) {
        const updatedProduct = {
          ...product,
          ...formData,
          qty: parseInt(formData.qty) || 1
        };
        onUpdateProduct(updatedProduct);
      }

      // If we have an orderId, call the API to update all products
      if (orderId && allProducts.length > 0) {
        // Add 0.5s delay before API call
        await new Promise(resolve => setTimeout(resolve, 500));

        // Format all products for API call, ensuring we include updated serials
        const formattedProducts = allProducts.map(p => {
          // If this is the current product being edited, use the updated formData
          if (p._id === product._id) {
            return {
              productId: p.id,
              productCode: formData.SKU || p.SKU,
              productName: formData.name || p.name,
              quantity: parseInt(formData.qty) || p.qty || 1,
              serials: formData.serials || p.serials || [],
              serialsConfirm: formData.serialsConfirm || p.serialsConfirm || []
            };
          }
          // For other products, use their current data
          return {
            productId: p.id,
            productCode: p.SKU,
            productName: p.name,
            quantity: p.qty || 1,
            serials: p.serials || [],
            serialsConfirm: p.serialsConfirm || []
          };
        });
        await updateTransfer(orderId, {
          items: formattedProducts,
          notes: 'Updated transfer'
        });
        // alert('Update transfer payload');
        // await updateOrderPurchases(orderId, formattedProducts);
        showSuccess('Transfer order updated successfully!');

        // Refresh the form data
        if (onRefreshData) {
          await onRefreshData();
        }
      }

      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.error_message || error.message || 'Failed to update transfer order';
      showError(errorMessage);
      // Still close the modal even if API call fails
      onClose();
    }
  };

  const handleCancel = () => {
    if (product) {
      setFormData({
        SKU: product.SKU || '',
        name: product.name || '',
        id: product.id || product._id || '',
        qty: product.qty || 1,
        serials: product.serials || [],
        serialsConfirm: product.serialsConfirm || [],
        rfidDevice: product.rfidDevice || ''
      });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box className="flex justify-between items-center">
          <Box>
            <Box className="font-bold text-xl">
              Product Details
            </Box>
            <Typography variant="body2" className="text-gray-600">
              {product?.name || 'Product Information'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Product Information Header */}
        <Card className="mb-6">
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <Box className="text-center">
                  <Avatar
                    src={getProductImageSrc(product)}
                    sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                  >
                    <Inventory />
                  </Avatar>
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="SKU"
                      value={formData.SKU}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Product Name"
                      value={formData.name}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Quantity"
                      type="number"
                      value={formData.qty}
                      onChange={(e) => handleInputChange('qty', parseInt(e.target.value) || 1)}
                      fullWidth
                      inputProps={{ min: 1 }}
                      variant="outlined"
                      disabled={!canEditProductQuantity}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Serial Numbers Management */}
        <Card className="mb-6">
          <CardContent>
            <Typography variant="h6" className="font-semibold mb-2">
              Serial Numbers Management ({formData.serials.length}
              {formData.serialsConfirm && formData.serialsConfirm.length > 0 && (
                <span className="text-green-600"> - {formData.serialsConfirm.length} Confirmed</span>
              )})
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="caption">
                <strong>Tag Filtering Rules:</strong> Only tags for SKU <strong>{product?.SKU}</strong> will be recorded.
                Tags already in active tag list, duplicate tags, or tags already used in other products will be automatically filtered out.
              </Typography>
            </Alert>

            {/* Quantity vs Serial Count Comparison */}
            <Box className="mb-4">
              {(() => {
                const quantity = parseInt(formData.qty) || 0;
                const serialCount = formData.serials.length;
                const difference = serialCount - quantity;

                if (difference === 0) {
                  return (
                    <Typography variant="body2" className="text-green-600 font-medium">
                      ✓ Quantity matches serial count ({quantity} = {serialCount})
                    </Typography>
                  );
                } else if (difference < 0) {
                  return (
                    <Typography variant="body2" className="text-red-600 font-medium">
                      ⚠ Missing {Math.abs(difference)} serial number{Math.abs(difference) !== 1 ? 's' : ''} (Need {quantity}, have {serialCount})
                    </Typography>
                  );
                } else {
                  return (
                    <Typography variant="body2" className="text-yellow-600 font-medium">
                      ⚠ Excess {difference} serial number{difference !== 1 ? 's' : ''} (Need {quantity}, have {serialCount})
                    </Typography>
                  );
                }
              })()}
            </Box>

            {/* Add New Serial */}
            {orderStatus === 'pending' && (
              <Box className="mb-4">
                <TextField
                  label="Add Serial Number"
                  value={newSerial}
                  onChange={(e) => setNewSerial(e.target.value)}
                  onKeyPress={handleKeyPress}
                  inputRef={(ref) => setInputRef(ref)}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleAddSerial}
                          disabled={
                            !newSerial.trim() ||
                            formData.serials.includes(newSerial.trim()) ||
                            allOrderSerials.includes(newSerial.trim()) ||
                            skipSerial.includes(newSerial.trim()) ||
                            activeTags.includes(newSerial.trim()) ||
                            (Object.keys(tagToSKU).length > 0 && (!tagToSKU[newSerial.trim()] || tagToSKU[newSerial.trim()] !== formData.SKU))
                          }
                        >
                          <Add />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    (() => {
                      const trimmedSerial = newSerial.trim();
                      if (!trimmedSerial) return '';
                      if (formData.serials.includes(trimmedSerial)) return 'Serial already exists in this product - will be cleared on Enter';
                      if (allOrderSerials.includes(trimmedSerial)) return 'Serial already exists in another product - will be cleared on Enter';
                      if (skipSerial.includes(trimmedSerial)) return 'Serial is in skip list - will be cleared on Enter';
                      if (activeTags.includes(trimmedSerial)) return 'Serial is in active tags (skip list) - will be cleared on Enter';
                      // Check tagToSKU mapping
                      if (Object.keys(tagToSKU).length > 0) {
                        const tagSKU = tagToSKU[trimmedSerial];
                        if (!tagSKU) return 'Tag not found in system - will be cleared on Enter';
                        if (tagSKU !== formData.SKU) return `Tag belongs to SKU ${tagSKU}, not ${formData.SKU} - will be cleared on Enter`;
                      }
                      return '';
                    })()
                  }
                />
              </Box>
            )}

            {/* Serial Status Legend */}
            {formData.serials.length > 0 && formData.serialsConfirm && formData.serialsConfirm.length > 0 && (
              <Box className="mb-3 flex items-center gap-4" sx={{ padding: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" className="font-semibold">
                  Legend:
                </Typography>
                <Box className="flex items-center gap-1">
                  <Box sx={{ width: 16, height: 16, backgroundColor: '#e8f5e9', border: '1px solid #4caf50', borderRadius: '4px' }} />
                  <Typography variant="caption">Confirmed</Typography>
                </Box>
                <Box className="flex items-center gap-1">
                  <Box sx={{ width: 16, height: 16, backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px' }} />
                  <Typography variant="caption">Not Confirmed</Typography>
                </Box>
              </Box>
            )}

            {/* Serial List as Chips */}
            <Box
              sx={{
                maxHeight: 300,
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                padding: 2,
                backgroundColor: '#fafafa',
                minHeight: 100
              }}
            >
              {formData.serials.length === 0 ? (
                <Typography variant="body2" color="textSecondary" className="text-center">
                  No serial numbers added yet
                </Typography>
              ) : (
                <Box className="flex flex-wrap gap-2">
                  {formData.serials.map((serial, index) => {
                    const isConfirmed = formData.serialsConfirm?.includes(serial);
                    return (
                      <Chip
                        key={index}
                        label={serial}
                        onDelete={orderStatus === 'pending' ? () => handleRemoveSerial(serial) : undefined}
                        color={isConfirmed ? 'success' : 'primary'}
                        variant="outlined"
                        sx={{
                          backgroundColor: isConfirmed ? '#e8f5e9' : '#e3f2fd',
                          borderColor: isConfirmed ? '#4caf50' : '#2196f3',
                          '& .MuiChip-deleteIcon': {
                            color: '#666',
                            '&:hover': {
                              color: '#f44336'
                            }
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* RFID Device Selection */}
        <Card className="mb-6">
          <CardContent>
            <Typography variant="h6" className="font-semibold mb-2">
              RFID Device Selection & MQTT Listening
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="caption">
                <strong>Important:</strong> Tags scanned via MQTT will only be recorded for the current product (SKU: <strong>{product?.SKU}</strong>).
                Make sure to select the correct RFID device and scan only tags for this SKU.
              </Typography>
            </Alert>
            {rfidDevices.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No RFID devices available. Please go to Settings to select devices first.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select RFID Device</InputLabel>
                <Select
                  value={formData.rfidDevice}
                  onChange={(e) => handleInputChange('rfidDevice', e.target.value)}
                  label="Select RFID Device"
                >
                  {rfidDevices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {formData.rfidDevice && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Selected Device: {getDeviceName(formData.rfidDevice)} ({formData.rfidDevice})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MQTT Status: {isConnected ? 'Connected' : 'Disconnected'}
                </Typography>
                {currentDeviceTopic && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                    Listening to topic: {currentDeviceTopic}
                  </Typography>
                )}
                {subscribedTopics.length > 0 && (
                  <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>
                    Subscribed Topics: {subscribedTopics.join(', ')}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions>
        <Box className="flex gap-2 w-full justify-end">
          {orderStatus === 'pending' ? (
            <>
              <Button onClick={handleCancel} variant="outlined">
                Cancel
              </Button>
              {canSaveProduct && (
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                  className="bg-blue-600"
                >
                  Save
                </Button>
              )}
            </>
          ) : (
            <Button onClick={onClose} variant="outlined">
              Close
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// Product Detail Dialog Component
const ProductDetailDialog = ({ open, onClose, product, onUpdateProduct, isEdit = false, existingProducts = [] }) => {
  const [skuSearchTerm, setSkuSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    if (product) {
      // If editing existing product, populate the form
      setSelectedProducts([{
        SKU: product.SKU,
        name: product.name,
        image_url: product.image_url,
        id: product.id || product._id,
        qty: product.qty || 1
      }]);
      setSkuSearchTerm(product.SKU);
    } else {
      // Reset form for new product
      setSelectedProducts([]);
      setSkuSearchTerm('');
      setSearchResults([]);
    }
  }, [product]);

  const handleSKUSearch = async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await lookupSKU(term);
      if (response.found && response.list) {
        setSearchResults(response.list.map(item => ({
          SKU: item.sku,
          name: item.name,
          image_url: item.image,
          id: item.id
        })));
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching SKU:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchTermChange = (value) => {
    setSkuSearchTerm(value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for 200ms delay
    const timeout = setTimeout(() => {
      handleSKUSearch(value);
    }, 200);

    setSearchTimeout(timeout);
  };
  window.handleSearchTermChange = handleSearchTermChange;
  const handleProductSelect = (productData) => {
    // Check if product is already selected in the dialog
    const isAlreadySelected = selectedProducts.some(p => p.id === productData.id);

    // Check if product already exists in the main product list
    const alreadyExists = existingProducts.some(p => p.id === productData.id);

    if (!isAlreadySelected && !alreadyExists) {
      setSelectedProducts(prev => [...prev, {
        ...productData,
        qty: 1
      }]);
    } else if (alreadyExists) {
      // If product already exists, show a message or handle accordingly
      // Product already exists in the order
    }

    setSearchResults([]);
  };

  const handleQuantityChange = (productId, newQuantity) => {
    setSelectedProducts(prev =>
      prev.map(product =>
        product.id === productId
          ? { ...product, qty: Math.max(1, newQuantity) }
          : product
      )
    );
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(product => product.id !== productId));
  };

  const handleSave = () => {
    if (selectedProducts.length > 0 && onUpdateProduct) {
      // If editing single product, return the first selected product
      if (product) {
        const productToSave = {
          ...selectedProducts[0],
          _id: product._id || `temp_${Date.now()}`
        };
        onUpdateProduct(productToSave);
      } else {
        // If adding new products, return all selected products as an array
        const productsToSave = selectedProducts.map(productData => ({
          ...productData,
          _id: `temp_${Date.now()}_${productData.id}`
        }));
        onUpdateProduct(productsToSave);
      }
    }
    onClose();
  };

  const handleCancel = () => {
    if (product) {
      // Reset to original product data
      setSelectedProducts([{
        SKU: product.SKU,
        name: product.name,
        image_url: product.image_url,
        id: product.id || product._id,
        qty: product.qty || 1
      }]);
      setSkuSearchTerm(product.SKU);
    } else {
      // Reset form for new product
      setSelectedProducts([]);
      setSkuSearchTerm('');
    }
    setSearchResults([]);
    onClose();
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box className="flex justify-between items-center">
          <Box>
            <Box className="font-bold text-xl">
              {product ? 'Edit Product' : 'Add New Product'}
            </Box>
            <Typography variant="body2" className="text-gray-600">
              {product ? product.name : 'Search and select products by SKU'}
            </Typography>
          </Box>
          <Box className="flex gap-2">
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              className="bg-blue-600"
              disabled={selectedProducts.length === 0}
            >
              {product ? 'Save' : `Add ${selectedProducts.length} Product${selectedProducts.length !== 1 ? 's' : ''}`}
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* SKU Search */}
        <Card className="mb-6">
          <CardContent>
            <Typography variant="h6" className="font-semibold mb-4">
              Search Product by SKU
            </Typography>
            <TextField
              label="Enter SKU"
              value={skuSearchTerm}
              onChange={(e) => handleSearchTermChange(e.target.value)}
              fullWidth
              placeholder="Type SKU to search..."
              InputProps={{
                endAdornment: isSearching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Box className="mt-4">
                <Typography variant="subtitle2" className="font-semibold mb-2">
                  Search Results ({searchResults.length})
                </Typography>
                <Paper className="max-h-60 overflow-y-auto">
                  <List>
                    {searchResults.map((result, index) => {
                      const isSelected = selectedProducts.some(p => p.id === result.id);
                      const alreadyExists = existingProducts.some(p => p.id === result.id);
                      return (
                        <ListItem
                          key={index}
                          button
                          onClick={() => handleProductSelect(result)}
                          className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''} ${alreadyExists ? 'bg-red-50' : ''}`}
                          disabled={isSelected || alreadyExists}
                        >
                          <Avatar
                            src={getProductImageSrc(result)}
                            sx={{ width: 50, height: 50, mr: 2 }}
                          >
                            <Inventory />
                          </Avatar>
                          <ListItemText
                            primary={result.name}
                            secondary={`SKU: ${result.SKU}`}
                          />
                          {isSelected && (
                            <Chip label="Selected" size="small" color="primary" />
                          )}
                          {alreadyExists && (
                            <Chip label="Already in Order" size="small" color="error" />
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              </Box>
            )}

            {skuSearchTerm.length >= 2 && searchResults.length === 0 && !isSearching && (
              <Typography variant="body2" color="textSecondary" className="mt-2">
                No products found for SKU: {skuSearchTerm}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Selected Products */}
        {selectedProducts.length > 0 && (
          <Card className="mb-6">
            <CardContent>
              <Typography variant="h6" className="font-semibold mb-4">
                Selected Products ({selectedProducts.length})
              </Typography>
              <Paper className="max-h-60 overflow-y-auto">
                <List>
                  {selectedProducts.map((productData, index) => (
                    <ListItem key={productData.id} className="hover:bg-gray-50">
                      <Avatar
                        src={getProductImageSrc(productData)}
                        sx={{ width: 50, height: 50, mr: 2 }}
                      >
                        <Inventory />
                      </Avatar>
                      <ListItemText
                        primary={productData.name}
                        secondary={`SKU: ${productData.SKU} | ID: ${productData.id}`}
                      />
                      <Box className="flex items-center gap-2">
                        <TextField
                          label="Qty"
                          type="number"
                          value={productData.qty}
                          onChange={(e) => handleQuantityChange(productData.id, parseInt(e.target.value) || 1)}
                          size="small"
                          sx={{ width: 80 }}
                          inputProps={{ min: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveProduct(productData.id)}
                          color="error"
                          title="Remove Product"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </CardContent>
          </Card>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const InventoryTransferCreatePage = () => {
  const { transferId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getStoreList, hasFrontendPermission } = useAppData();
  const { showError, showSuccess, message, type, isVisible, hideMessage } = useFlashMessage();
  const { subscribe, unsubscribe, isConnected } = useMQTT();
  const { selectedDeviceObjects, getDeviceName } = useDeviceSelection();
  const readerDevices = React.useMemo(
    () =>
      (selectedDeviceObjects || []).filter((device) =>
        (device?.id || '').toLowerCase().startsWith('desktop')
      ),
    [selectedDeviceObjects]
  );
  const handheldDevices = React.useMemo(
    () =>
      (selectedDeviceObjects || []).filter((device) =>
        (device?.id || '').toLowerCase().startsWith('handheld')
      ),
    [selectedDeviceObjects]
  );

  // Generate default transfer ID using current timestamp
  const generateDefaultTransferId = () => {
    const timestamp = Date.now();
    return `TRF-${timestamp}`;
  };

  const [transferData, setTransferData] = useState({
    unique_key: generateDefaultTransferId(),
    status: 'pending',
    fromBranch: '',
    toBranch: '',
    receiver: '',
    requestedBy: 'Current User',
    createdAt: new Date().toISOString(),
    createdBy: 'Current User'
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [showProductDetailModal, setShowProductDetailModal] = useState(false);
  const [isSerialEditingLocked, setIsSerialEditingLocked] = useState(['VerifiedSerials', 'confirmed', 'completed'].includes(transferData.status));
  const [isEditMode, setIsEditMode] = useState(true);
  const canEditTransferProducts = hasFrontendPermission(
    FEATURE_PERMISSIONS.INVENTORY_TRANSFER.EDIT_PRODUCTS
  );
  const canConfirmTransferDataPermission = hasFrontendPermission(
    FEATURE_PERMISSIONS.INVENTORY_TRANSFER.CONFIRM_TRANSFER_DATA
  );
  const canImportTransferData = hasFrontendPermission(
    FEATURE_PERMISSIONS.INVENTORY_TRANSFER.IMPORT_TRANSFER_DATA
  );

  // Users list for receiver dropdown
  const [usersList, setUsersList] = useState([]);

  const receiverSelectValue = React.useMemo(() => {
    if (!transferData.receiver) return '';
    return usersList.some(user => user.id === transferData.receiver)
      ? transferData.receiver
      : '';
  }, [transferData.receiver, usersList]);

  // CSV Import states
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [csvImportProgress, setCsvImportProgress] = useState('');
  const [csvImportError, setCsvImportError] = useState(null);
  const [csvImportSuccess, setCsvImportSuccess] = useState(null);
  const [missingSKUs, setMissingSKUs] = useState([]);
  const [pendingValidProducts, setPendingValidProducts] = useState([]);
  const [showMissingSKUsModal, setShowMissingSKUsModal] = useState(false);

  // Delete transfer states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Order creation states
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);

  // Confirm & Proceed functionality
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmStep, setConfirmStep] = useState(null); // null, 'validation', 'processing', 'result'
  const [validationResult, setValidationResult] = useState(null); // 'success', 'error'
  const [validationMessage, setValidationMessage] = useState('');
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Active tags state
  const [activeTags, setActiveTags] = useState([]);

  // Tag to SKU mapping state
  const [tagToSKU, setTagToSKU] = useState({});

  // Scan to Confirm & Import functionality
  const [showScanImportDialog, setShowScanImportDialog] = useState(false);
  const [scanDialogMode, setScanDialogMode] = useState(null); // 'confirm_transfer' | 'import'
  const [scanProductsSnapshot, setScanProductsSnapshot] = useState([]);
  const [scannedProductTags, setScannedProductTags] = useState({}); // { productId: [tags] }
  const [allProductsMatched, setAllProductsMatched] = useState(false);
  const [selectedScanReaderDevice, setSelectedScanReaderDevice] = useState('');
  const [selectedScanHandheldDevice, setSelectedScanHandheldDevice] = useState('');
  const [showMatchCompleteDialog, setShowMatchCompleteDialog] = useState(false);
  const scanActiveDevices = React.useMemo(
    () => [selectedScanReaderDevice, selectedScanHandheldDevice].filter(Boolean),
    [selectedScanReaderDevice, selectedScanHandheldDevice]
  );
  const hasScanActiveDevice = scanActiveDevices.length > 0;

  // OTP Confirmation
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [otpValue, setOtpValue] = useState('');

  // Main page MQTT listening states
  const [mainPageDevice, setMainPageDevice] = useState('');
  const [handheldDevice, setHandheldDevice] = useState('');
  const [lastMqttSignalTime, setLastMqttSignalTime] = useState(null);
  const [lastSignalDevice, setLastSignalDevice] = useState('');
  const productRowRefs = React.useRef({});
  const [signalColorTrigger, setSignalColorTrigger] = useState(0);

  // Message hashmap: key = timestamp (ms), value = message
  const messageHashMapRef = React.useRef(new Map());
  const [uniqueMessagesCount, setUniqueMessagesCount] = useState(0);
  const [showTagListModal, setShowTagListModal] = useState(false);

  // Tag to SKU mapping for main page MQTT
  const [mainPageTagToSKU, setMainPageTagToSKU] = useState({});
  const [skuMetadata, setSkuMetadata] = useState({});
  const [branchTagStats, setBranchTagStats] = useState(null);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);

  // End & Import To POS states
  const [showEndImportDialog, setShowEndImportDialog] = useState(false);
  const [confirmingProducts, setConfirmingProducts] = useState([]);
  const [showAllConfirmedDialog, setShowAllConfirmedDialog] = useState(false);
  const statusPollingIntervalRef = useRef(null);
  const hasShownVerifiedSerialsMessageRef = useRef(false);

  const shouldLockSerialsByStatus = React.useMemo(
    () => ['VerifiedSerials', 'confirmed'].includes(transferData.status),
    [transferData.status]
  );

  useEffect(() => {
    if (showEndImportDialog) {
      if (isSerialEditingLocked) {
        setIsSerialEditingLocked(false);
      }
      return;
    }

    if (isSerialEditingLocked !== shouldLockSerialsByStatus) {
      setIsSerialEditingLocked(shouldLockSerialsByStatus);
    }
  }, [showEndImportDialog, shouldLockSerialsByStatus, isSerialEditingLocked]);

  const mapTransferResponseToState = (response = {}) => {
    const mappedTransferData = {
      id: response._id,
      unique_key: response.transferCode || '',
      status: response.status || 'pending',
      fromBranch: response.fromBranchName || '',
      toBranch: response.toBranchName || '',
      receiver: response.receivedByUserId || '',
      requestedBy: response.createdByUserName || 'Current User',
      createdAt: response.createdAt || new Date().toISOString(),
      createdBy: response.createdByUserName || 'Current User',
      fromBranchId: response.fromBranchId,
      toBranchId: response.toBranchId,
      receivedByUserId: response.receivedByUserId,
      notes: response.notes || ''
    };

    const mappedProducts = (response.items || []).map(item => ({
      _id: item._id || `temp_${Date.now()}_${item.productId}`,
      id: item.productId,
      SKU: item.productCode,
      name: item.productName,
      qty: item.quantity,
      serials: item.serials || [],
      serialsConfirm: item.serialsConfirm || [],
      image_url: item.productImage || item.image || ''
    }));

    return { mappedTransferData, mappedProducts };
  };

  const applyTransferApiResponse = useCallback((response, { updateEditMode = true } = {}) => {
    const { mappedTransferData, mappedProducts } = mapTransferResponseToState(response);
    setTransferData(mappedTransferData);
    setProducts(mappedProducts);

    if (updateEditMode) {
      setIsEditMode(response?.status === 'pending');
    }

    return response?.status;
  }, []);

  // Load existing transfer if editing
  const loadTransferDetails = async () => {
    if (!transferId) return;

    setLoading(true);
    setError(null);
    try {
      // Call real API
      const response = await getTransferDetail(transferId);

      if (response) {
        applyTransferApiResponse(response, { updateEditMode: true });
      } else {
        setError('Transfer not found');
      }
    } catch (err) {
      setError('Failed to load transfer details. Please try again.');
      console.error('Error loading transfer details:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshTransferStatusSilently = useCallback(async () => {
    if (!transferId) return null;

    try {
      const response = await getTransferDetail(transferId);
      if (response) {
        return applyTransferApiResponse(response, { updateEditMode: false });
      }
    } catch (err) {
      console.error('[Transfer Status Polling] Failed to refresh transfer status:', err);
    }

    return null;
  }, [transferId, applyTransferApiResponse]);

  const stopStatusPolling = () => {
    if (statusPollingIntervalRef.current) {
      clearInterval(statusPollingIntervalRef.current);
      statusPollingIntervalRef.current = null;
    }
  };

  // Poll transfer status when status is 'confirmed' until it becomes 'VerifiedSerials'
  useEffect(() => {
    if (transferId && transferData.status === 'confirmed') {
      // Reset the message flag when starting polling
      hasShownVerifiedSerialsMessageRef.current = false;

      // Start polling every 2 seconds
      statusPollingIntervalRef.current = setInterval(async () => {
        const newStatus = await refreshTransferStatusSilently();

        if (newStatus === 'VerifiedSerials') {
          // Stop polling
          stopStatusPolling();

          // Show success message only once
          if (!hasShownVerifiedSerialsMessageRef.current) {
            hasShownVerifiedSerialsMessageRef.current = true;
            showSuccess('Verify success');
          }
        }
      }, 2000);
    } else {
      // Stop polling if status is not 'confirmed' or no transferId
      stopStatusPolling();
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      stopStatusPolling();
    };
  }, [transferId, transferData.status, showSuccess, refreshTransferStatusSilently]);

  useEffect(() => {
    loadTransferDetails();
  }, [transferId]);

  // Fetch users list on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getSimpleUserList();
        if (response && Array.isArray(response)) {
          setUsersList(response);
        }
      } catch (error) {
        console.error('Failed to fetch users list:', error);
        // Keep empty array if fetch fails
        setUsersList([]);
      }
    };

    fetchUsers();
  }, []);

  // Listen for MQTT messages when scan dialog is open
  useEffect(() => {
    if (!showScanImportDialog) {
      return undefined;
    }

    window.addEventListener('mqttMessage', handleMQTTMessage);
    return () => {
      window.removeEventListener('mqttMessage', handleMQTTMessage);
    };
  }, [showScanImportDialog, scanActiveDevices]);

  const isScanDialogActive = showScanImportDialog || showEndImportDialog;

  useEffect(() => {
    if (!isScanDialogActive || !isConnected || scanActiveDevices.length === 0) {
      return undefined;
    }

    scanActiveDevices.forEach((deviceId) => subscribe(deviceId));
    return () => {
      scanActiveDevices.forEach((deviceId) => unsubscribe(deviceId));
    };
  }, [isScanDialogActive, isConnected, scanActiveDevices, subscribe, unsubscribe]);

  const fetchTagMapping = useCallback(async (branchName) => {
    if (!branchName) {
      setBranchTagStats(null);
      return;
    }

    try {
      const storeList = getStoreList();
      const fromStore = storeList.find(s => s.name === branchName);
      const clientId = fromStore?.id || null;

      const response = await getAllTagInSystem(clientId);
      if (response && response.tagToSKU) {
        const tagMap = response.tagToSKU;
        const tagCount = Object.keys(tagMap).length;
        const skuCount = new Set(Object.values(tagMap)).size;

        setMainPageTagToSKU(tagMap);
        setSkuMetadata(response.skuCodeDataMap || {});
        setBranchTagStats({
          branchName,
          tagCount,
          skuCount
        });
        console.log(`Set branch ${branchName}  tag map`, tagMap);
      } else {
        setMainPageTagToSKU({});
        setSkuMetadata({});
        setBranchTagStats(null);
      }
    } catch (error) {
      console.error('[Tag Mapping] Failed to fetch tag-to-SKU mapping:', error);
      setBranchTagStats(null);
    }
  }, [getStoreList]);

  // Fetch tag-to-SKU mapping whenever From Branch changes
  useEffect(() => {
    fetchTagMapping(transferData.fromBranch);
  }, [transferData.fromBranch, fetchTagMapping]);

  // Listen for MQTT messages on main page
  const activeMqttDevices = React.useMemo(
    () => [mainPageDevice, handheldDevice].filter(Boolean),
    [mainPageDevice, handheldDevice]
  );
  const hasActiveMqttDevice = activeMqttDevices.length > 0;

  useEffect(() => {
    const savedReader = localStorage.getItem('rfid_device_mainPageTransfer');
    if (savedReader) {
      setMainPageDevice(savedReader);
    }

    const savedHandheld = localStorage.getItem('rfid_device_handheldTransfer');
    if (savedHandheld) {
      setHandheldDevice(savedHandheld);
    }
  }, []);

  // Subscribe to MQTT whenever tracked devices or connection status change
  useEffect(() => {
    if (!isConnected || activeMqttDevices.length === 0) {
      return undefined;
    }

    activeMqttDevices.forEach((deviceId) => subscribe(deviceId));

    return () => {
      activeMqttDevices.forEach((deviceId) => unsubscribe(deviceId));
    };
  }, [activeMqttDevices, isConnected, subscribe, unsubscribe]);

  // Check if all products have EXACT matching serials (no excess, no missing)
  const checkAllProductsComplete = (productsList) => {
    if (!productsList || productsList.length === 0) {
      return false;
    }

    return productsList.every(product => {
      const expectedQty = product.qty || 0;
      const serialCount = (product.serials || []).length;
      return serialCount === expectedQty; // Must be exact match
    });
  };

  // Scroll to product row when a tag is scanned
  const scrollToProductRow = (productId) => {
    const rowRef = productRowRefs.current[productId];
    if (rowRef) {
      rowRef.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Add highlight effect
      rowRef.style.backgroundColor = '#dbeafe'; // Light blue
      setTimeout(() => {
        rowRef.style.backgroundColor = '';
      }, 2000);
    }
  };

  // Fill serial number into product list based on SKU
  const fillSerialNumberToProductList = useCallback((serialNumber) => {
    if (isSerialEditingLocked) {
      return;
    }

    if (showScanImportDialog && scanDialogMode === 'confirm_transfer') {
      return;
    }

    if (showEndImportDialog) {
      return;
    }

    // Look up the SKU for this tag
    const sku = mainPageTagToSKU[serialNumber];

    if (!sku) {
      // showError(`Tag ${serialNumber} is not registered in the system`);
      return;
    }

    // Find the product with this SKU
    const productIndex = products.findIndex(p => p.SKU === sku);

    if (productIndex === -1) {
      showError(`Product ${sku} is not in this transfer order`);
      return;
    }

    const product = products[productIndex];

    // Check if serial already exists in product
    if (product.serials && product.serials.includes(serialNumber)) {
      // showError(`Tag already scanned for this product`);
      return;
    }

    // Add serial to product
    setProducts(prevProducts => {
      const updatedProducts = [...prevProducts];
      const currentProduct = updatedProducts[productIndex];

      updatedProducts[productIndex] = {
        ...currentProduct,
        serials: [...(currentProduct.serials || []), serialNumber]
      };

      // Scroll to the matched product row
      scrollToProductRow(currentProduct.id || currentProduct.SKU);

      // Check if all products are now complete
      if (checkAllProductsComplete(updatedProducts)) {
        // Delay 1 second before showing modal and auto-save
        // setTimeout(() => {
        //   handleImportCompletion();
        // }, 3000);
      }

      return updatedProducts;
    });
  }, [mainPageTagToSKU, products, showError, showScanImportDialog, scanDialogMode, showEndImportDialog, isSerialEditingLocked]);

  // Handle MQTT message for main page listening
  const handleMainPageMQTTMessage = useCallback(
    (event) => {
      try {
        const { topic, message } = event.detail;
        const isTrackedDevice = activeMqttDevices.includes(topic);

        if (isTrackedDevice) {
          const currentTime = Date.now();
          setLastMqttSignalTime(currentTime);
          setLastSignalDevice(topic);

          const messageStr =
            typeof message === 'string' ? message.trim() : String(message).trim();
          if (messageStr) {
            messageHashMapRef.current.set(currentTime, messageStr);
          }

          fillSerialNumberToProductList(message);
        }
      } catch (error) {
        console.error('[Main Page MQTT] Error parsing MQTT message:', error);
      }
    },
    [activeMqttDevices, fillSerialNumberToProductList]
  );

  // Main page MQTT message listener
  useEffect(() => {
    if (!hasActiveMqttDevice) {
      return undefined;
    }

    window.addEventListener('mqttMessage', handleMainPageMQTTMessage);
    return () => {
      window.removeEventListener('mqttMessage', handleMainPageMQTTMessage);
    };
  }, [hasActiveMqttDevice, handleMainPageMQTTMessage]);

  // Update signal status color every second without causing infinite loop
  useEffect(() => {
    if (hasActiveMqttDevice && lastMqttSignalTime) {
      const interval = setInterval(() => {
        setSignalColorTrigger(prev => prev + 1);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [hasActiveMqttDevice, lastMqttSignalTime]);

  // Clean up old messages from hashmap every 10 seconds
  useEffect(() => {
    if (hasActiveMqttDevice) {
      const cleanupInterval = setInterval(() => {
        const currentTime = Date.now();
        const cutoffTime = currentTime - 10000;

        for (const [timestamp] of messageHashMapRef.current.entries()) {
          if (timestamp < cutoffTime) {
            messageHashMapRef.current.delete(timestamp);
          }
        }
      }, 10000);

      return () => clearInterval(cleanupInterval);
    }
  }, [hasActiveMqttDevice]);

  // Calculate unique messages array from hashmap
  useEffect(() => {
    if (hasActiveMqttDevice) {
      const updateUniqueMessages = () => {
        const uniqueMessagesSet = new Set(messageHashMapRef.current.values());
        setUniqueMessagesCount(uniqueMessagesSet.size);
      };

      updateUniqueMessages();
      const updateInterval = setInterval(updateUniqueMessages, 1000);
      return () => clearInterval(updateInterval);
    }

    setUniqueMessagesCount(0);
    messageHashMapRef.current.clear();
  }, [hasActiveMqttDevice]);

  // Clean up product row refs when products change
  useEffect(() => {
    // Clear refs that no longer exist in products list
    const currentProductIds = products.map(p => p.id || p.SKU);
    const refKeys = Object.keys(productRowRefs.current);

    refKeys.forEach(key => {
      if (!currentProductIds.includes(key)) {
        delete productRowRefs.current[key];
      }
    });
  }, [products]);

  const handleBack = () => {
    navigate('/inventory-transfer');
  };

  const handleFromBranchChange = useCallback((newFromBranch) => {
    setTransferData(prev => {
      const storeList = getStoreList();
      const alternativeStore = storeList.find(store => store.name !== newFromBranch);
      const nextToBranch =
        prev.toBranch && prev.toBranch !== newFromBranch
          ? prev.toBranch
          : alternativeStore?.name || '';

      return {
        ...prev,
        fromBranch: newFromBranch,
        toBranch: nextToBranch
      };
    });

    fetchTagMapping(newFromBranch);
  }, [getStoreList, fetchTagMapping]);

  const handleViewProduct = async (product) => {
    try {
      // Fetch active tags before opening the modal
      const activeTagsResponse = await getAllActiveTags();
      if (activeTagsResponse && activeTagsResponse.tags) {
        setActiveTags(activeTagsResponse.tags);
      }

      // Get From Branch ID from store list
      const storeList = getStoreList();
      const fromStore = storeList.find(s => s.name === transferData.fromBranch);
      const clientId = fromStore?.id || null;

      // Fetch tag to SKU mapping from the API with clientId
      const tagMappingResponse = await getAllTagInSystem(clientId);
      if (tagMappingResponse && tagMappingResponse.tagToSKU) {
        setTagToSKU(tagMappingResponse.tagToSKU);
      }
    } catch (error) {
      console.error('Error fetching tag data:', error);
      // Continue with opening modal even if API call fails
      setActiveTags([]);
      setTagToSKU({});
    }

    setSelectedProduct(product);
    setShowProductDetailModal(true);
  };

  // Get all serial numbers from all products in the transfer (excluding current product)
  const getAllTransferSerials = (currentProductId) => {
    return products
      .filter(p => p._id !== currentProductId)
      .flatMap(p => p.serials || []);
  };

  const handleCloseProductDetailModal = () => {
    setShowProductDetailModal(false);
    setSelectedProduct(null);
  };

  const handleCloseProductDetail = () => {
    setShowProductDetail(false);
    setSelectedProduct(null);
  };

  const handleUpdateProductFromModal = (updatedProduct) => {
    const updatedProducts = products.map(product =>
      product._id === updatedProduct._id ? updatedProduct : product
    );
    setProducts(updatedProducts);
  };

  const handleAddNewProduct = () => {
    setSelectedProduct(null);
    setShowProductDetail(true);
  };

  const handleUpdateProduct = async (updatedProduct) => {
    try {
      let updatedProducts;

      if (selectedProduct) {
        // Update existing product
        updatedProducts = products.map(product =>
          product._id === updatedProduct._id ? updatedProduct : product
        );
        setProducts(updatedProducts);
      } else {
        // Add new product(s) - handle both single and multiple products
        if (Array.isArray(updatedProduct)) {
          // Handle multiple products and prevent duplicates
          setProducts(prev => {
            let newProducts = [...prev];

            updatedProduct.forEach(newProduct => {
              // Check if product with same ID already exists
              const existingIndex = newProducts.findIndex(p => p.id === newProduct.id);

              if (existingIndex !== -1) {
                // Replace existing product with new one (last input takes precedence)
                newProducts[existingIndex] = newProduct;
              } else {
                // Add new product
                newProducts.push(newProduct);
              }
            });

            updatedProducts = newProducts;
            return newProducts;
          });
        } else {
          // Handle single product
          setProducts(prev => {
            const existingIndex = prev.findIndex(p => p.id === updatedProduct.id);

            if (existingIndex !== -1) {
              // Replace existing product with new one (last input takes precedence)
              const newProducts = [...prev];
              newProducts[existingIndex] = updatedProduct;
              updatedProducts = newProducts;
              return newProducts;
            } else {
              // Add new product
              updatedProducts = [...prev, updatedProduct];
              return updatedProducts;
            }
          });
        }
      }

      // If editing existing transfer, update via API
      if (transferId && updatedProducts && updatedProducts.length > 0) {
        // Format products for API call with correct field names
        const formattedProducts = updatedProducts.map(product => ({
          productId: product.id,
          productCode: product.SKU,
          productName: product.name,
          quantity: product.qty || 1,
          serials: product.serials || [],
          serialsConfirm: product.serialsConfirm || []
        }));

        // Update transfer via API
        await updateTransfer(transferId, {
          items: formattedProducts,
          notes: transferData.notes || ''
        });

        showSuccess('Product added/updated successfully!');

        // Refresh the transfer data
        await loadTransferDetails();
      }

      // Check if we should create transfer order automatically (for new transfers)
      if (!transferId) {
        checkAndCreateTransferOrder();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update product';
      showError(errorMessage);

      // Revert the local state change if API call failed
      if (transferId) {
        await loadTransferDetails();
      }
    }
  };

  // Function to check conditions and create transfer order automatically
  const checkAndCreateTransferOrder = async () => {
    // Only create transfer order if:
    // 1. We have a transfer ID (unique_key)
    // 2. We have more than 1 product
    // 3. We're not in edit mode for existing transfer
    // 4. We haven't already created a transfer order
    // 5. We're not currently creating one
    // 6. We have both from and to branches selected
    if (transferData.unique_key && products.length > 1 && !transferId && !orderCreated && !isCreatingOrder && transferData.fromBranch && transferData.toBranch) {
      try {
        await createTransferOrderAutomatically();
      } catch (error) {
        // Don't show error here as it might be called multiple times
      }
    }
  };

  // Function to create transfer order automatically
  const createTransferOrderAutomatically = async () => {
    try {
      setIsCreatingOrder(true);
      setLoading(true);

      // Format products according to transfer structure
      const formattedProducts = products.map(product => ({
        id: product.id,
        SKU: product.SKU,
        qty: product.qty || 1,
        name: product.name,
        image_url: product.image_url || '',
        serials: product.serials || [],
        serialsConfirm: product.serialsConfirm || []
      }));

      // Format transfer data according to transfer structure
      const transferToCreate = {
        unique_key: transferData.unique_key,
        products: formattedProducts,
        status: 'pending',
        fromBranch: transferData.fromBranch,
        toBranch: transferData.toBranch,
        receiver: transferData.receiver,
        requestedBy: transferData.requestedBy,
        estimatedDelivery: transferData.estimatedDelivery,
        metaData: {
          transferType: 'branch_to_branch'
        }
      };

      // Placeholder for transfer creation API call
      // const response = await createTransferOrder(transferToCreate);

      // For now, simulate successful creation
      const response = { _id: `transfer_${Date.now()}` };

      if (response && response._id) {
        setOrderCreated(true);
        showSuccess('Transfer order created successfully!');
        // Navigate to update page with the created transfer ID
        navigate(`/inventory-transfer/${response._id}`, {
          state: {
            fromCreate: true
          }
        });
      } else {
        throw new Error('Invalid response from server');
      }

    } catch (error) {
      const errorMessage = error.response?.data?.error_message || error.message || 'Failed to create transfer order';
      showError(errorMessage);
    } finally {
      setIsCreatingOrder(false);
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      // Remove product from local state first
      const updatedProducts = products.filter(product => product._id !== productId);
      setProducts(updatedProducts);

      if (transferId && updatedProducts.length >= 0) {
        // Format products for API call with correct field names
        const formattedProducts = updatedProducts.map(product => ({
          productId: product.id,
          productCode: product.SKU,
          productName: product.name,
          quantity: product.qty || 1,
          serials: product.serials || [],
          serialsConfirm: product.serialsConfirm || []
        }));

        // Update transfer via API
        await updateTransfer(transferId, {
          items: formattedProducts,
          notes: transferData.notes || ''
        });

        showSuccess('Product deleted successfully!');

        // Refresh the transfer data
        await loadTransferDetails();
      } else if (!transferId) {
        // For new transfers, just show success message
        showSuccess('Product removed from transfer!');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete product';
      showError(errorMessage);

      // Revert the local state change if API call failed
      await loadTransferDetails();
    }
  };

  const handleSaveTransfer = async () => {
    try {
      setLoading(true);

      // Validate required fields
      if (!transferData.fromBranch || !transferData.toBranch || !transferData.receiver) {
        showError('Please select From Branch, To Branch, and Receiver');
        return;
      }

      if (transferData.fromBranch === transferData.toBranch) {
        showError('From and To branches cannot be the same');
        return;
      }

      if (products.length === 0) {
        showError('Please add at least one product to the transfer');
        return;
      }

      // Get branch IDs from store list
      const storeList = getStoreList();
      const fromStore = storeList.find(s => s.name === transferData.fromBranch);
      const toStore = storeList.find(s => s.name === transferData.toBranch);

      if (!fromStore || !toStore) {
        showError('Invalid branch selection');
        return;
      }

      // Get receiver user info from users list
      const selectedUser = usersList.find(u => u.id === transferData.receiver);
      if (!selectedUser) {
        showError('Invalid receiver selection');
        return;
      }

      // Format items according to API spec
      const formattedItems = products.map(product => ({
        productId: product.id,
        productCode: product.SKU,
        productName: product.name,
        quantity: product.qty || 1,
        serials: product.serials || [],
        serialsConfirm: product.serialsConfirm || []
      }));

      if (transferId) {
        // Update existing transfer (only items and notes)
        const updatePayload = {
          items: formattedItems,
          notes: transferData.notes || ''
        };

        await updateTransfer(transferId, updatePayload);
        showSuccess('Transfer updated successfully!');
      } else {
        // Create new transfer
        const createPayload = {
          transferCode: transferData.unique_key,
          fromBranchId: fromStore.id,
          fromBranchName: fromStore.name,
          toBranchId: toStore.id,
          toBranchName: toStore.name,
          receivedByUserId: selectedUser.id,
          receivedByUserName: selectedUser.name,
          items: formattedItems,
          notes: transferData.notes || ''
        };

        const response = await createTransfer(createPayload);
        showSuccess('Transfer created successfully!');

        // Navigate to the created transfer detail page
        if (response && response._id) {
          navigate(`/inventory-transfer/${response._id}`);
          return;
        }
      }

      navigate('/inventory-transfer');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save transfer';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (transferId) {
      loadTransferDetails(); // Reload original data
    } else {
      // Clear form for new transfer with default transfer ID
      setTransferData({
        unique_key: generateDefaultTransferId(),
        status: 'pending',
        fromBranch: '',
        toBranch: '',
        receiver: '',
        requestedBy: 'Current User',
        createdAt: new Date().toISOString(),
        createdBy: 'Current User'
      });
      setProducts([]);
    }
    setIsEditMode(false);
  };

  // Handle Save Transfer Information (metadata only)
  const handleSaveTransferInfo = async () => {
    try {
      setLoading(true);

      // Validate required fields
      if (!transferData.fromBranch || !transferData.toBranch || !transferData.receiver) {
        showError('Please select From Branch, To Branch, and Receiver');
        return;
      }

      if (transferData.fromBranch === transferData.toBranch) {
        showError('From and To branches cannot be the same');
        return;
      }

      // Get branch IDs from store list
      const storeList = getStoreList();
      const fromStore = storeList.find(s => s.name === transferData.fromBranch);
      const toStore = storeList.find(s => s.name === transferData.toBranch);

      if (!fromStore || !toStore) {
        showError('Invalid branch selection');
        return;
      }

      // Get receiver user info from users list
      const selectedUser = usersList.find(u => u.id === transferData.receiver);
      if (!selectedUser) {
        showError('Invalid receiver selection');
        return;
      }

      // Update transfer metadata (branches and receiver info)
      const updatePayload = {
        fromBranchId: fromStore.id,
        fromBranchName: fromStore.name,
        toBranchId: toStore.id,
        toBranchName: toStore.name,
        receivedByUserId: selectedUser.id,
        receivedByUserName: selectedUser.name,
        // Keep existing items - don't modify them
        items: products.map(product => ({
          productId: product.id,
          productCode: product.SKU,
          productName: product.name,
          quantity: product.qty || 1,
          serials: product.serials || [],
          serialsConfirm: product.serialsConfirm || []
        }))
      };

      await updateTransfer(transferId, updatePayload);
      showSuccess('Transfer information updated successfully!');

      // Reload transfer data to get updated values
      await loadTransferDetails();

      setIsEditMode(false);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update transfer information';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const prefillScanDevices = useCallback(() => {
    const savedReader = localStorage.getItem('rfid_device_scanReaderTransfer');
    const savedHandheld = localStorage.getItem('rfid_device_scanHandheldTransfer');
    const legacyDevice = getSelectedRfidDevice('transfer');

    const resolvedReader = savedReader || mainPageDevice || legacyDevice || '';
    const resolvedHandheld = savedHandheld || handheldDevice || '';

    setSelectedScanReaderDevice(resolvedReader);
    setSelectedScanHandheldDevice(resolvedHandheld);

    if (resolvedReader) {
      saveSelectedRfidDevice(resolvedReader, 'transfer');
    }
  }, [handheldDevice, mainPageDevice]);

  // Handle Open Scan Import Dialog
  const handleOpenScanImport = (mode = 'import') => {
    setScanDialogMode(mode);
    // Create a snapshot of current products to lock data during scanning
    const snapshot = products.map(product => ({
      ...product,
      serials: [...(product.serials || [])],
      serialsConfirm: [...(product.serialsConfirm || [])]
    }));
    setScanProductsSnapshot(snapshot);

    // Initialize scanned tags for locked products
    const initialScannedTags = {};
    snapshot.forEach(product => {
      initialScannedTags[product.id || product.SKU] = [];
    });
    setScannedProductTags(initialScannedTags);
    setAllProductsMatched(false);

    prefillScanDevices();
    setShowScanImportDialog(true);
  };

  // Handle MQTT message from RFID devices selected in the scan dialog
  const handleMQTTMessage = (event) => {
    try {
      const { topic, message } = event.detail;

      if (scanActiveDevices.length > 0 && scanActiveDevices.includes(topic)) {
        handleTagScanned(message);
      }
    } catch (error) {
      console.error('[MQTT] Error parsing MQTT message:', error);
    }
  };

  // Handle device selections inside the scan dialog
  const handleScanReaderDeviceChange = (deviceTopic) => {
    setSelectedScanReaderDevice(deviceTopic);
    if (deviceTopic) {
      localStorage.setItem('rfid_device_scanReaderTransfer', deviceTopic);
      saveSelectedRfidDevice(deviceTopic, 'transfer');
    } else {
      localStorage.removeItem('rfid_device_scanReaderTransfer');
      saveSelectedRfidDevice('', 'transfer');
    }
  };

  const handleScanHandheldDeviceChange = (deviceTopic) => {
    setSelectedScanHandheldDevice(deviceTopic);
    if (deviceTopic) {
      localStorage.setItem('rfid_device_scanHandheldTransfer', deviceTopic);
    } else {
      localStorage.removeItem('rfid_device_scanHandheldTransfer');
    }
  };

  // Handle Close Scan Import Dialog
  const handleCloseScanImport = () => {
    // Unsubscribe from MQTT when closing
    if (scanActiveDevices.length > 0) {
      scanActiveDevices.forEach((deviceId) => unsubscribe(deviceId));
    }

    setShowScanImportDialog(false);
    setScannedProductTags({});
    setAllProductsMatched(false);
    setScanDialogMode(null);
    setScanProductsSnapshot([]);
  };

  // Check if all products are matched
  const checkAllProductsMatched = (scannedTags, referenceProducts = (scanProductsSnapshot.length > 0 ? scanProductsSnapshot : products)) => {
    if (!referenceProducts || referenceProducts.length === 0) {
      return false;
    }

    const allMatched = referenceProducts.every(product => {
      const productId = product.id || product.SKU;
      const expectedCount = product.qty || 0;
      const scannedCount = (scannedTags[productId] || []).length;
      return scannedCount >= expectedCount;
    });

    // If just completed (changed from false to true)
    if (allMatched && !allProductsMatched) {

      // Unsubscribe from MQTT
      if (scanActiveDevices.length > 0) {
        scanActiveDevices.forEach((deviceId) => unsubscribe(deviceId));
      }

      // Show flash dialog for 2 seconds
      setShowMatchCompleteDialog(true);
      setTimeout(() => {
        setShowMatchCompleteDialog(false);
      }, 2000);
    }

    setAllProductsMatched(allMatched);
    return allMatched;
  };

  // Handle main page device selection
  const handleMainPageDeviceChange = (deviceTopic) => {
    setMainPageDevice(deviceTopic);
    if (deviceTopic) {
      localStorage.setItem('rfid_device_mainPageTransfer', deviceTopic);
    } else {
      localStorage.removeItem('rfid_device_mainPageTransfer');
    }
  };

  const handleHandheldDeviceChange = (deviceTopic) => {
    setHandheldDevice(deviceTopic);
    if (deviceTopic) {
      localStorage.setItem('rfid_device_handheldTransfer', deviceTopic);
    } else {
      localStorage.removeItem('rfid_device_handheldTransfer');
    }
  };

  // Check if transfer data is complete and ready for confirmation
  const isTransferReadyForConfirmation = () => {
    // Check basic transfer information
    if (!transferData.fromBranch || !transferData.toBranch || !transferData.receiver) {
      return false;
    }

    // Check if products exist
    if (!products || products.length === 0) {
      return false;
    }

    // Check if all products have exact matching quantities
    return checkAllProductsComplete(products);
  };

  // Handle Confirm Transfer Data - change status to confirmed
  const handleConfirmTransferData = async () => {
    if (!transferId) {
      showError('Cannot confirm: Transfer ID is missing');
      return;
    }

    if (!isTransferReadyForConfirmation()) {
      // showError('Transfer is not ready for confirmation. Please ensure all products have exact matching serials.');
      return;
    }

    try {
      setLoading(true);

      // Prepare data for confirmation
      const storeList = getStoreList();
      const fromStore = storeList.find(s => s.name === transferData.fromBranch);
      const toStore = storeList.find(s => s.name === transferData.toBranch);
      const selectedUser = usersList.find(u => u.id === transferData.receiver);

      if (!fromStore || !toStore || !selectedUser) {
        showError('Invalid transfer data');
        return;
      }

      // Format items for API
      const formattedItems = products.map(product => ({
        productId: product.id,
        productCode: product.SKU,
        productName: product.name,
        quantity: product.qty || 1,
        serials: product.serials || [],
        serialsConfirm: product.serialsConfirm || []
      }));

      // STEP 1: Update transfer with latest serials BEFORE confirming
      const updatePayload = {
        fromBranchId: fromStore.id,
        fromBranchName: fromStore.name,
        toBranchId: toStore.id,
        toBranchName: toStore.name,
        receivedByUserId: selectedUser.id,
        receivedByUserName: selectedUser.name,
        items: formattedItems,
        notes: transferData.notes || ''
      };

      await updateTransfer(transferId, updatePayload);

      // STEP 2: Confirm the transfer
      const confirmationData = {
        items: products.map(product => ({
          productId: product.id,
          productCode: product.SKU,
          productName: product.name,
          confirmedQuantity: (product.serials || []).length,
          confirmedSerials: product.serials || [],
          missingSerials: [],
          missingReason: ''
        })),
        confirmationNotes: 'Transfer confirmed with RFID scanning - all products matched',
        missingItemsReason: ''
      };

      await confirmTransfer(transferId, confirmationData);

      showSuccess('Transfer confirmed successfully!');

      // Reload transfer data
      await loadTransferDetails();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to confirm transfer';
      showError(errorMessage);
      console.error('[Confirm Transfer] Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Process Import - complete the transfer
  const handleProcessImport = async () => {
    if (!transferId) {
      showError('Cannot process: Transfer ID is missing');
      return;
    }

    try {
      setLoading(true);

      // Call API to complete transfer
      await completeTransfer(transferId);

      showSuccess('Transfer completed and imported successfully!');

      // Reload transfer data
      await loadTransferDetails();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to complete transfer';
      showError(errorMessage);
      console.error('[Process Import] Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle completion - stop listening and auto-save
  const handleImportCompletion = async () => {
    // Unsubscribe from MQTT
    [mainPageDevice, handheldDevice].filter(Boolean).forEach((deviceId) => {
      unsubscribe(deviceId);
    });

    // Show completion dialog
    setShowCompletionDialog(true);

    // Auto-save after 2 seconds
    setTimeout(async () => {
      setShowCompletionDialog(false);
      await handleAutoSave();
    }, 2000);
  };

  // Auto-save function
  const handleAutoSave = async () => {
    if (!transferId) {
      showError('Cannot save: Transfer ID is missing');
      return;
    }

    try {
      setAutoSaving(true);

      // Prepare data for update
      const storeList = getStoreList();
      const fromStore = storeList.find(s => s.name === transferData.fromBranch);
      const toStore = storeList.find(s => s.name === transferData.toBranch);
      const selectedUser = usersList.find(u => u.id === transferData.receiver);

      if (!fromStore || !toStore || !selectedUser) {
        showError('Invalid transfer data');
        return;
      }

      // Format items for API
      const formattedItems = products.map(product => ({
        productId: product.id,
        productCode: product.SKU,
        productName: product.name,
        quantity: product.qty || 1,
        serials: product.serials || [],
        serialsConfirm: product.serialsConfirm || []
      }));

      // Update transfer
      const updatePayload = {
        fromBranchId: fromStore.id,
        fromBranchName: fromStore.name,
        toBranchId: toStore.id,
        toBranchName: toStore.name,
        receivedByUserId: selectedUser.id,
        receivedByUserName: selectedUser.name,
        items: formattedItems,
        notes: transferData.notes || ''
      };

      await updateTransfer(transferId, updatePayload);

      showSuccess('Transfer data saved automatically!');

      // Reload current page data instead of navigating away
      await loadTransferDetails();

    } catch (error) {
      showError('Failed to auto-save transfer data');
      console.error('[Auto-save] Error:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  // 8 8 17 11
  // Set ref for product row (using useCallback to prevent recreation)
  const setProductRowRef = React.useCallback((productId) => {
    return (ref) => {
      if (ref) {
        productRowRefs.current[productId] = ref;
      } else {
        delete productRowRefs.current[productId];
      }
    };
  }, []);

  // Handle MQTT tag scan (simulated or real)
  const handleTagScanned = (tagId) => {
    // Find which product this tag belongs to
    // Only match tags that exist in product.serials (tags có trong order)
    let matchedProductId = null;
    let matchedProduct = null;

    const referenceProducts = (showScanImportDialog && scanProductsSnapshot.length > 0)
      ? scanProductsSnapshot
      : products;

    for (const product of referenceProducts) {
      if (product.serials && product.serials.includes(tagId)) {
        matchedProductId = product.id || product.SKU;
        matchedProduct = product;
        break;
      }
    }

    if (matchedProductId && matchedProduct) {
      setScannedProductTags(prev => {
        const updated = { ...prev };
        if (!updated[matchedProductId]) {
          updated[matchedProductId] = [];
        }

        // Only add if not already scanned (avoid duplicate)
        if (!updated[matchedProductId].includes(tagId)) {
          updated[matchedProductId] = [...updated[matchedProductId], tagId];

          // Calculate progress
          const newScannedQty = updated[matchedProductId].length;
          const expectedQty = matchedProduct.qty || 0;
          const isComplete = newScannedQty >= expectedQty;

          // Show success feedback with progress
          if (isComplete) {
            // showSuccess(`✓ ${matchedProduct.name || matchedProduct.SKU} - Complete! (${newScannedQty}/${expectedQty})`);
          } else {
            // showSuccess(`Tag scanned: ${matchedProduct.name || matchedProduct.SKU} (${newScannedQty}/${expectedQty})`);
          }
        } else {
          // Tag already scanned - show warning
        }

        checkAllProductsMatched(updated);
        return updated;
      });
    } else {
      // Tag not found in any product's serial list - only accept tags in order
    }
  };

  // Handle Confirm Transfer (after scan complete)
  const handleConfirmTransfer = () => {
    setShowScanImportDialog(false);
    setShowOTPDialog(true);
  };

  const handleScanDialogPrimaryAction = async () => {
    if (scanDialogMode === 'confirm_transfer') {
      handleCloseScanImport();
      await handleConfirmTransferData();
      return;
    }

    handleConfirmTransfer();
  };

  // Handle OTP Submit
  const handleOTPSubmit = async () => {
    if (otpValue === '0000') {
      try {
        setLoading(true);

        // Build confirmation data from scanned products
        const confirmationData = {
          items: products.map(product => {
            const productId = product.id || product.SKU;
            const scannedTags = scannedProductTags[productId] || [];
            const expectedQty = product.qty || 0;
            const confirmedQty = scannedTags.length;

            // Calculate missing serials
            const allSerials = product.serials || [];
            const missingSerials = allSerials.filter(serial => !scannedTags.includes(serial));

            return {
              productId: product.id,
              productCode: product.SKU,
              productName: product.name,
              confirmedQuantity: confirmedQty,
              confirmedSerials: scannedTags,
              missingSerials: missingSerials,
              missingReason: missingSerials.length > 0 ? 'Not scanned during confirmation' : ''
            };
          }),
          confirmationNotes: 'Transfer confirmed via RFID scanning',
          missingItemsReason: ''
        };

        // Call API to confirm transfer
        await confirmTransfer(transferId, confirmationData);

        showSuccess('Transfer confirmed successfully!');
        setShowOTPDialog(false);
        setOtpValue('');

        // Reload transfer data
        await loadTransferDetails();
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to confirm transfer';
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    } else {
      showError('Invalid OTP! Please enter 0000');
    }
  };

  // Handle End & Import To POS
  const handleEndImportToPOS = async () => {
    // Initialize confirmingProducts with current products
    const productsToConfirm = products.map(p => ({
      ...p,
      serialsConfirm: p.serialsConfirm || []
    }));
    setConfirmingProducts(productsToConfirm);

    prefillScanDevices();

    // Show the confirmation modal
    setShowEndImportDialog(true);
  };

  // Complete the transfer after all serials are confirmed
  const completeEndImportToPOS = async () => {
    try {
      setLoading(true);

      // Update transfer with serialsConfirm data
      const formattedItems = confirmingProducts.map(product => ({
        productId: product.id,
        productCode: product.SKU,
        productName: product.name,
        quantity: product.qty || 1,
        serials: product.serials || [],
        serialsConfirm: product.serialsConfirm || []
      }));

      await updateTransfer(transferId, {
        items: formattedItems
      });

      // Call API to complete transfer
      await completeTransfer(transferId);

      showSuccess('Transfer completed and imported to POS successfully!');

      // Close modal and navigate
      setShowEndImportDialog(false);
      navigate('/inventory-transfer');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to complete transfer';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle MQTT message for End & Import confirmation
  const handleEndImportMqttMessage = (message) => {
    const scannedSerial = message.trim();

    if (!scannedSerial) return;

    // Find which product this serial belongs to
    let productFound = false;
    const updatedProducts = confirmingProducts.map(product => {
      // Check if this serial exists in the product's serials array
      if (product.serials && product.serials.includes(scannedSerial)) {
        // Check if not already in serialsConfirm
        if (!product.serialsConfirm.includes(scannedSerial)) {
          productFound = true;
          // showSuccess(`✓ Confirmed ${scannedSerial} for ${product.name}`);
          return {
            ...product,
            serialsConfirm: [...product.serialsConfirm, scannedSerial]
          };
        } else {
          // showError(`Serial ${scannedSerial} already confirmed`);
        }
      }
      return product;
    });

    if (productFound) {
      setConfirmingProducts(updatedProducts);

      // Check if all serials are confirmed
      const allConfirmed = updatedProducts.every(product => {
        const serialsCount = product.serials?.length || 0;
        const confirmedCount = product.serialsConfirm?.length || 0;
        return confirmedCount === serialsCount;
      });

      if (allConfirmed) {
        // Show success modal
        setShowAllConfirmedDialog(true);
      }
    } else {
      // showError(`Serial ${scannedSerial} not found in transfer products`);
    }
  };

  // Subscribe to MQTT for End & Import modal - use current listening devices automatically
  useEffect(() => {
    if (!showEndImportDialog || !isConnected || scanActiveDevices.length === 0) {
      return undefined;
    }

    const handleMqttEvent = (event) => {
      try {
        const { topic, message } = event.detail;
        const isRelevantTopic = scanActiveDevices.includes(topic);

        if (isRelevantTopic) {
          const messageStr = message.toString();
          handleEndImportMqttMessage(messageStr);
        }
      } catch (error) {
        console.error('[End Import Modal] Error handling MQTT message:', error);
      }
    };

    window.addEventListener('mqttMessage', handleMqttEvent);
    return () => {
      window.removeEventListener('mqttMessage', handleMqttEvent);
    };
  }, [showEndImportDialog, isConnected, scanActiveDevices, confirmingProducts]);

  // CSV Import Functions (similar to import page)
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const skuIndex = headers.findIndex(h => h.includes('sku'));
    const qtyIndex = headers.findIndex(h => h.includes('qty') || h.includes('quantity'));

    if (skuIndex === -1) {
      throw new Error('CSV file must contain a SKU column');
    }
    if (qtyIndex === -1) {
      throw new Error('CSV file must contain a QTY or Quantity column');
    }

    const csvData = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const sku = values[skuIndex];
      const qty = parseInt(values[qtyIndex]) || 1;

      if (sku) {
        csvData.push({ SKU: sku, qty: qty });
      }
    }

    return csvData;
  };

  const handleCSVImport = async (file) => {
    if (!file) return;

    setCsvImportLoading(true);
    setCsvImportError(null);
    setCsvImportSuccess(null);
    setCsvImportProgress('Reading CSV file...');

    try {
      const csvText = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      setCsvImportProgress('Parsing CSV data...');
      const csvData = parseCSV(csvText);

      if (csvData.length === 0) {
        throw new Error('No valid data found in CSV file');
      }

      setCsvImportProgress(`Looking up ${csvData.length} products...`);

      // Prepare data for batch lookup API
      const lookupData = csvData.map(item => ({
        sku: item.SKU,
        qty: parseInt(item.qty) || 1
      }));

      // Call batch lookup API
      const response = await lookupManySKU(lookupData);

      if (!response.list || response.list.length === 0) {
        throw new Error('No products found for the provided SKUs');
      }

      setCsvImportProgress('Processing products...');

      // Identify SKUs that were not returned by API
      const returnedSKUs = response.list.map(product => product.sku);
      const requestedSKUs = csvData.map(item => item.SKU);
      const notFoundSKUs = requestedSKUs.filter(sku => !returnedSKUs.includes(sku));

      // Convert API response to product format
      const validProducts = response.list.map(productData => ({
        SKU: productData.sku,
        name: productData.name,
        image_url: productData.image,
        id: productData.id,
        qty: productData.qty,
        _id: `temp_${Date.now()}_${productData.id}`,
        serials: [],
        serialsConfirm: []
      }));

      // If there are missing SKUs, surface the modal to continue/clear
      if (notFoundSKUs.length > 0) {
        setCsvImportLoading(false);
        setCsvImportProgress('');
        setMissingSKUs(notFoundSKUs);
        setPendingValidProducts(validProducts);
        setShowMissingSKUsModal(true);
        return;
      }

      setCsvImportProgress('Adding products to transfer...');

      // Handle duplicates: replace quantity but keep existing serials
      setProducts(prev => {
        let updatedProducts = [...prev];

        validProducts.forEach(newProduct => {
          const existingIndex = updatedProducts.findIndex(p => p.id === newProduct.id);

          if (existingIndex !== -1) {
            // Product exists: update quantity but keep existing serials
            updatedProducts[existingIndex] = {
              ...updatedProducts[existingIndex],
              qty: newProduct.qty
            };
          } else {
            // New product: add to list
            updatedProducts.push(newProduct);
          }
        });

        return updatedProducts;
      });

      const newProductsCount = validProducts.filter(p =>
        !products.some(existing => existing.id === p.id)
      ).length;

      const updatedProductsCount = validProducts.length - newProductsCount;

      let successMessage = '';
      if (newProductsCount > 0 && updatedProductsCount > 0) {
        successMessage = `Successfully imported ${newProductsCount} new products and updated ${updatedProductsCount} existing products`;
      } else if (newProductsCount > 0) {
        successMessage = `Successfully imported ${newProductsCount} new products`;
      } else if (updatedProductsCount > 0) {
        successMessage = `Successfully updated ${updatedProductsCount} existing products`;
      } else {
        successMessage = 'All products from CSV already exist in the transfer';
      }

      setCsvImportSuccess(successMessage);

    } catch (error) {
      console.error('CSV import error:', error);
      setCsvImportError(error.message || 'Failed to import CSV file');
    } finally {
      setCsvImportLoading(false);
      setCsvImportProgress('');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      handleCSVImport(file);
    } else {
      setCsvImportError('Please select a valid CSV file');
    }
    // Reset file input
    event.target.value = '';
  };

  const handleSkipMissingSkus = () => {
    if (pendingValidProducts.length === 0) {
      setShowMissingSKUsModal(false);
      setMissingSKUs([]);
      return;
    }

    let previousProductsSnapshot = [];
    setProducts(prev => {
      previousProductsSnapshot = prev;
      const updatedProducts = [...prev];

      pendingValidProducts.forEach(newProduct => {
        const existingIndex = updatedProducts.findIndex(p => p.id === newProduct.id);

        if (existingIndex !== -1) {
          updatedProducts[existingIndex] = {
            ...updatedProducts[existingIndex],
            qty: newProduct.qty
          };
        } else {
          updatedProducts.push(newProduct);
        }
      });

      return updatedProducts;
    });

    const newProductsCount = pendingValidProducts.filter(p =>
      !previousProductsSnapshot.some(existing => existing.id === p.id)
    ).length;
    const updatedProductsCount = pendingValidProducts.length - newProductsCount;

    let successMessage = '';
    if (newProductsCount > 0 && updatedProductsCount > 0) {
      successMessage = `Imported ${newProductsCount} new products and updated ${updatedProductsCount} existing products. ${missingSKUs.length} SKU(s) were not found.`;
    } else if (newProductsCount > 0) {
      successMessage = `Imported ${newProductsCount} new products. ${missingSKUs.length} SKU(s) were not found.`;
    } else if (updatedProductsCount > 0) {
      successMessage = `Updated ${updatedProductsCount} existing products. ${missingSKUs.length} SKU(s) were not found.`;
    } else {
      successMessage = `${missingSKUs.length} SKU(s) were not found in the system.`;
    }

    setCsvImportSuccess(successMessage);
    setShowMissingSKUsModal(false);
    setMissingSKUs([]);
    setPendingValidProducts([]);
  };

  const handleClearMissingSkus = () => {
    setShowMissingSKUsModal(false);
    setMissingSKUs([]);
    setPendingValidProducts([]);
    showError('Import cancelled. Please fix the CSV file and try again.');
  };

  // Delete Transfer Functions
  const handleDeleteTransfer = async () => {
    if (!transferId) return;

    setDeleteLoading(true);
    try {
      await deleteTransfer(transferId);
      showSuccess('Transfer deleted successfully!');
      navigate('/inventory-transfer');
    } catch (error) {
      console.error('Error deleting transfer:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete transfer';
      showError(errorMessage);
    } finally {
      setDeleteLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'completed':
      case 'done':
        return 'success';
      case 'pending':
      case 'prepared':
        return 'warning';
      case 'confirmed':
      case 'in_progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
      case 'completed':
      case 'done':
        return 'completed';
      case 'pending':
        return 'Pending';
      case 'prepared':
        return 'Prepared';
      case 'confirmed':
      case 'in_progress':
        return 'In Transit';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getTotalItems = () => {
    return products.reduce((total, product) => total + (product.qty || 0), 0);
  };

  // Check if Confirm & Proceed button should be disabled
  const isConfirmButtonDisabled = () => {
    // No products
    if (products.length === 0) return true;

    // No branches selected
    if (!transferData.fromBranch || !transferData.toBranch) return true;

    // Same branch selected for from and to
    if (transferData.fromBranch === transferData.toBranch) return true;

    // At least one product has actual qty = 0
    const hasZeroActualQty = products.some(product => (product.serials ? product.serials.length : 0) === 0);
    if (hasZeroActualQty) return true;

    // More than 20% products are not matching
    const notMatchingProducts = products.filter(product =>
      (product.qty || 0) !== (product.serials ? product.serials.length : 0)
    );
    const notMatchingPercentage = (notMatchingProducts.length / products.length) * 100;
    if (notMatchingPercentage > 20) return true;

    return false;
  };

  // Handle Confirm & Proceed button click
  const handleConfirmAndProceed = () => {
    setShowConfirmDialog(true);
    setConfirmStep(null);
    setSliderPosition(0);
  };

  // Handle Scan to Confirm button click
  const handleScanToConfirm = () => {
    setShowScanConfirmDialog(true);
    setScanConfirmStep('scanning');
    setScanValidationMessage('Please scan the transfer order barcode to confirm...');

    // Simulate scanning process
    setTimeout(() => {
      // Simulate successful scan
      setScanConfirmStep('result');
      setScanValidationResult('success');
      setScanValidationMessage('Transfer order scanned and confirmed successfully!');
      showSuccess('Transfer order confirmed via scan!');

      // Auto close after 2 seconds
      setTimeout(() => {
        setShowScanConfirmDialog(false);
        setScanConfirmStep(null);
        setScanValidationResult(null);
        setScanValidationMessage('');
      }, 2000);
    }, 3000);
  };

  // Handle API call for processing transfer order
  const handleProcessTransferOrder = async () => {
    try {
      setConfirmStep('validation');
      setValidationMessage('Validating transfer data...');

      // Show validation for exactly 2 seconds
      setTimeout(async () => {
        setConfirmStep('processing');
        setValidationMessage('Processing transfer order...');

        try {
          // Call complete transfer API
          await completeTransfer(transferId || transferData.id);

          // Show success result
          setTimeout(() => {
            setConfirmStep('result');
            setValidationResult('success');
            setValidationMessage('Transfer order completed successfully!');

            // Show success message
            showSuccess('Transfer order completed successfully!');

            // Redirect to transfers list after 2 seconds
            setTimeout(() => {
              navigate('/inventory-transfer');
            }, 2000);
          }, 1000);

        } catch (error) {
          console.error('Error processing transfer order:', error);

          // Show error result
          setConfirmStep('result');
          setValidationResult('error');
          const errorMsg = error.response?.data?.message || error.message || 'Failed to process transfer order';
          setValidationMessage(errorMsg);

          // Show error message
          showError(errorMsg);
        }
      }, 2000);

    } catch (error) {
      console.error('Error in validation step:', error);
      setConfirmStep('result');
      setValidationResult('error');
      setValidationMessage('An error occurred during validation.');
      showError('An error occurred during validation.');
    }
  };

  // Handle iPhone-style slider mouse events
  const handleSliderMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleSliderMouseMove = (e) => {
    if (!isDragging) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));

    setSliderPosition(percentage);
  };

  const handleSliderMouseUp = (e) => {
    if (!isDragging) return;

    setIsDragging(false);

    // If slider reaches 100%, proceed to validation
    if (sliderPosition >= 95) {
      setSliderPosition(100);
      setShowConfirmDialog(false);

      // Start API call process after a short delay
      setTimeout(() => {
        handleProcessTransferOrder();
      }, 500);
    } else {
      // Snap back to 0 if not completed
      setSliderPosition(0);
    }
  };

  // Handle iPhone-style slider touch events
  const handleSliderTouchStart = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleSliderTouchMove = (e) => {
    if (!isDragging) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));

    setSliderPosition(percentage);
  };

  const handleSliderTouchEnd = (e) => {
    if (!isDragging) return;

    setIsDragging(false);

    // If slider reaches 100%, proceed to validation
    if (sliderPosition >= 95) {
      setSliderPosition(100);
      setShowConfirmDialog(false);

      // Start API call process after a short delay
      setTimeout(() => {
        handleProcessTransferOrder();
      }, 500);
    } else {
      // Snap back to 0 if not completed
      setSliderPosition(0);
    }
  };

  // Reset confirmation dialog
  const resetConfirmDialog = () => {
    setShowConfirmDialog(false);
    setConfirmStep(null);
    setValidationResult(null);
    setValidationMessage('');
    setSliderPosition(0);
    setIsDragging(false);
  };

  const isConfirmTransferMode = scanDialogMode === 'confirm_transfer';
  const modalProducts = scanProductsSnapshot.length > 0 ? scanProductsSnapshot : products;
  const totalExpectedSerials = modalProducts.reduce((total, product) => total + (product.qty || 0), 0);
  const totalScannedSerials = modalProducts.reduce((total, product) => {
    const productId = product.id || product.SKU;
    return total + ((scannedProductTags[productId] || []).length);
  }, 0);
  const overallProgressPercent = totalExpectedSerials > 0 ? Math.min(100, (totalScannedSerials / totalExpectedSerials) * 100) : 0;
  const scanDialogTitle = isConfirmTransferMode ? 'Confirm Transfer Data' : 'Scan To Confirm & Import Transfer';
  const scanDialogDescription = isConfirmTransferMode
    ? 'Scan RFID tags for each product. Once every product matches its quantity and serial list, the transfer data can be confirmed.'
    : 'Scan RFID tags for each product. When all products are matched, you can confirm the transfer.';
  const scanDialogSuccessMessage = isConfirmTransferMode
    ? 'All products have been scanned successfully! You can now confirm the transfer data.'
    : 'All products have been scanned successfully! You can now confirm the transfer.';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Back to Transfers
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box className="mb-6">
        <Box className="flex items-center gap-4 mb-4">
          <IconButton onClick={handleBack} className="text-gray-600">
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" className="font-bold text-gray-800">
              {transferId ? 'Edit Transfer Order' : 'Create New Transfer Order'}
            </Typography>
            <Typography variant="body1" className="text-gray-600">
              {transferId ? transferData.unique_key : 'Enter transfer details'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Transfer Workflow Status */}
      <Card className="mb-6" sx={{}}>
        <CardContent>
          <Typography variant="h6" className="font-semibold mb-4 text-white">
            Transfer Process Workflow
          </Typography>
          <Box className="flex flex-wrap items-center justify-between gap-4">
            {/* Step 1: Metadata */}
            <Box className="flex items-center gap-2">
              <Box
                className="flex items-center justify-center rounded-full"
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: transferData.fromBranch && transferData.toBranch && transferData.receiver ? '#4caf50' : '#fff',
                  color: transferData.fromBranch && transferData.toBranch && transferData.receiver ? '#fff' : '#666'
                }}
              >
                {transferData.fromBranch && transferData.toBranch && transferData.receiver ? (
                  <CheckCircle />
                ) : (
                  <Typography variant="body1" className="font-bold">1</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body2" className="font-semibold text-white">
                  Metadata
                </Typography>
                <Typography variant="caption" className="text-white opacity-80">
                  Branch & Receiver Info
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" className="text-white opacity-60">→</Typography>

            {/* Step 2: Add Products */}
            <Box className="flex items-center gap-2">
              <Box
                className="flex items-center justify-center rounded-full"
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: products.length > 0 ? '#4caf50' : '#fff',
                  color: products.length > 0 ? '#fff' : '#666'
                }}
              >
                {products.length > 0 ? (
                  <CheckCircle />
                ) : (
                  <Typography variant="body1" className="font-bold">2</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body2" className="font-semibold text-white">
                  Add Products
                </Typography>
                <Typography variant="caption" className="text-white opacity-80">
                  SKU & Quantities ({products.length} items)
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" className="text-white opacity-60">→</Typography>

            {/* Step 3: Scan Tags */}
            <Box className="flex items-center gap-2">
              <Box
                className="flex items-center justify-center rounded-full"
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: products.some(p => p.serials && p.serials.length > 0) ? '#4caf50' : '#fff',
                  color: products.some(p => p.serials && p.serials.length > 0) ? '#fff' : '#666'
                }}
              >
                {products.some(p => p.serials && p.serials.length > 0) ? (
                  <CheckCircle />
                ) : (
                  <Typography variant="body1" className="font-bold">3</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body2" className="font-semibold text-white">
                  Scan Tags
                </Typography>
                <Typography variant="caption" className="text-white opacity-80">
                  MQTT Tag Input ({products.reduce((sum, p) => sum + (p.serials?.length || 0), 0)} tags)
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" className="text-white opacity-60">→</Typography>

            {/* Step 4: Save & Process */}
            <Box className="flex items-center gap-2">
              <Box
                className="flex items-center justify-center rounded-full"
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: transferData.status === 'completed' || transferData.status === 'completed' ? '#4caf50' : '#fff',
                  color: transferData.status === 'completed' || transferData.status === 'completed' ? '#fff' : '#666'
                }}
              >
                {transferData.status === 'completed' || transferData.status === 'completed' ? (
                  <CheckCircle />
                ) : (
                  <Typography variant="body1" className="font-bold">4</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body2" className="font-semibold text-white">
                  Save & Process
                </Typography>
                <Typography variant="caption" className="text-white opacity-80">
                  {getStatusLabel(transferData.status)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Important Instructions */}
      {!transferId && (
        <Alert severity="info" className="mb-6" icon={<Info />}>
          <Typography variant="caption" className="text-gray-600 mt-2 block">
            Note: Tags are filtered per SKU - only tags matching the selected SKU and not already in the active tag list will be recorded.
          </Typography>
        </Alert>
      )}

      {/* Transfer Metadata */}
      <Card className="mb-6" sx={{ position: 'relative' }}>
        {/* Lock overlay when status is confirmed */}
        {transferData.status === 'confirmed' && transferId && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1
            }}
          />
        )}
        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
          <Box className="flex justify-between items-center mb-4">
            <Typography variant="h6" className="font-semibold">
              Step 1: Transfer Information
            </Typography>
            <Box className="flex gap-2">
              {transferId && (
                <Chip
                  label={getStatusLabel(transferData.status)}
                  color={getStatusColor(transferData.status)}
                  variant="filled"
                  className="font-semibold"
                />
              )}
              {isCreatingOrder && (
                <Chip
                  label="Creating Transfer Order..."
                  color="info"
                  variant="filled"
                  className="font-semibold"
                  icon={<CircularProgress size={16} />}
                />
              )}
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Transfer ID"
                value={transferData.unique_key}
                onChange={(e) => setTransferData(prev => ({ ...prev, unique_key: e.target.value }))}
                fullWidth
                disabled={!isEditMode || !!transferId || isCreatingOrder}
                helperText={
                  transferId
                    ? "Transfer ID cannot be changed"
                    : transferData.unique_key && products.length > 1 && !orderCreated
                      ? "Transfer order will be created automatically when you have more than 1 product"
                      : "Auto-generated with timestamp"
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Date Created"
                value={formatDate(transferData.createdAt)}
                fullWidth
                disabled
                helperText="Auto-generated"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Requested By"
                value={transferData.requestedBy}
                onChange={(e) => setTransferData(prev => ({ ...prev, requestedBy: e.target.value }))}
                fullWidth
                disabled={!isEditMode || (transferId && transferData.status !== 'pending') || (transferData.status === 'confirmed' && transferId)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={!isEditMode || (transferId && transferData.status !== 'pending') || (transferData.status === 'confirmed' && transferId)}>
                <InputLabel>From Branch</InputLabel>
                <Select
                  value={transferData.fromBranch}
                  onChange={(e) => handleFromBranchChange(e.target.value)}
                  label="From Branch"
                  startAdornment={<Store />}
                >
                  {getStoreList().map((store) => (
                    <MenuItem key={store.id} value={store.name}>
                      {store.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={!isEditMode || (transferId && transferData.status !== 'pending') || (transferData.status === 'confirmed' && transferId)}>
                <InputLabel>To Branch</InputLabel>
                <Select
                  value={transferData.toBranch}
                  onChange={(e) => setTransferData(prev => ({ ...prev, toBranch: e.target.value }))}
                  label="To Branch"
                  startAdornment={<Store />}
                >
                  {getStoreList().map((store) => (
                    <MenuItem
                      key={store.id}
                      value={store.name}
                      disabled={store.name === transferData.fromBranch}
                    >
                      {store.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={!isEditMode || (transferId && transferData.status !== 'pending') || (transferData.status === 'confirmed' && transferId)}>
                <InputLabel>Receiver</InputLabel>
                <Select
                  value={receiverSelectValue}
                  onChange={(e) => setTransferData(prev => ({ ...prev, receiver: e.target.value }))}
                  label="Receiver"
                  startAdornment={<Person />}
                >
                  <MenuItem value="">Select Receiver</MenuItem>
                  {usersList.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {branchTagStats && (
            <Box className="mt-4 p-3 rounded-md bg-gray-50 border border-gray-200">
              <Typography variant="subtitle2" className="font-semibold text-gray-700">
                Current branch overview
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                Branch {branchTagStats.branchName || 'N/A'} has {branchTagStats.tagCount} tags mapped across {branchTagStats.skuCount} SKUs.
              </Typography>
            </Box>
          )}

          {/* Save Transfer Information Button */}
          {transferId && (
            <Box className="flex justify-end mt-4 gap-2">
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveTransferInfo}
                disabled={!isEditMode || transferData.status !== 'pending' || isCreatingOrder}
                className="bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400"
              >
                Save Changes
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Lock Dialog when status is confirmed */}
      <Dialog
        open={transferData.status === 'confirmed' && !!transferId}
        onClose={() => { }} // Prevent closing
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress size={48} sx={{ mb: 3, color: '#2563eb' }} />
            <Typography variant="h6" className="font-semibold mb-2" align="center">
              Waiting for Serial Confirmation
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center" sx={{ whiteSpace: 'pre-line' }}>
              Sử dụng handheld để quét xác nhận serials.
              {'\n'}(Thao tác này được thực hiện tại địa điểm nhận hàng)
              {'\n'}(Trên app handheld thao tác:)
              {'\n'}(1. Vào tab "Scan Verify")
              {'\n'}(2. Chọn verify task ứng với transfer id này ({transferData.unique_key}))
              {'\n'}(3. Sử dụng handheld quét thùng hàng đến khi đạt 100%")
              {'\n'}(4. Nhấn submit để xác nhận chữ kí").
              {'\n'}(5. Dialog này sẽ tắt sau khi xác nhận thành công và bạn có thể nhập hàng vào POS").
            </Typography>
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, width: '100%' }}>
              <Typography variant="caption" color="textSecondary" align="center" display="block">
                The form is locked until serial confirmation is complete.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Products List */}
      <Card>
        <CardContent>
          <Box className="flex justify-between items-center mb-4">
            <Box className="flex items-center gap-4">
              <Typography variant="h6" className="font-semibold">
                Step 2 & 3: Products and RFID Tag Scanning ({products.length})
              </Typography>

              {/* Reader Device Dropdown */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Reader Device</InputLabel>
                <Select
                  value={mainPageDevice}
                  onChange={(e) => handleMainPageDeviceChange(e.target.value)}
                  label="Reader Device"
                  disabled={transferData.status === 'confirmed' || transferData.status === 'completed'}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {readerDevices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Handheld Device Dropdown */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Handheld Device</InputLabel>
                <Select
                  value={handheldDevice}
                  onChange={(e) => handleHandheldDeviceChange(e.target.value)}
                  label="Handheld Device"
                  disabled={transferData.status === 'confirmed' || transferData.status === 'completed'}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {handheldDevices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Last Signal Time */}
              {hasActiveMqttDevice && (
                <Box className="flex items-center gap-4">
                  <Box className="flex items-center gap-2">
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          lastMqttSignalTime && Date.now() - lastMqttSignalTime < 3000
                            ? '#16a34a'
                            : '#dc2626',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                      key={signalColorTrigger}
                    >
                      {lastMqttSignalTime ? (
                        <>
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor:
                                Date.now() - lastMqttSignalTime < 3000 ? '#16a34a' : '#dc2626',
                              display: 'inline-block'
                            }}
                          />
                          Last signal: {new Date(lastMqttSignalTime).toLocaleTimeString()}
                          {lastSignalDevice && (
                            <span>
                              {' '}
                              · {getDeviceName(lastSignalDevice) || lastSignalDevice}
                            </span>
                          )}
                        </>
                      ) : (
                        'Waiting for signal...'
                      )}
                    </Typography>
                  </Box>
                  {/* Số lượng item trong thùng */}
                  <Box className="flex items-center gap-2">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowTagListModal(true)}
                      sx={{
                        color: '#2563eb',
                        borderColor: '#2563eb',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#1d4ed8',
                          backgroundColor: '#eff6ff'
                        }
                      }}
                    >
                      Item Count: {uniqueMessagesCount}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
            <Box className="flex gap-2">
              {(transferData.status === 'pending' || !transferId) && isEditMode && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={handleAddNewProduct}
                    className="bg-green-600 text-white hover:bg-green-700"
                  >
                    Add Product
                  </Button>
                  {canEditTransferProducts && (
                    <Button
                      variant="outlined"
                      startIcon={<Upload />}
                      component="label"
                      disabled={csvImportLoading}
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Import CSV
                      <input
                        type="file"
                        accept=".csv"
                        hidden
                        onChange={handleFileUpload}
                      />
                    </Button>
                  )}
                </>
              )}
              {canImportTransferData && (transferData.status === 'confirmed' || transferData.status === 'VerifiedSerials') && transferId && (
                <Button
                  variant="contained"
                  startIcon={<LocalShipping />}
                  onClick={handleEndImportToPOS}
                  disabled={transferData.status === 'confirmed'}
                  className={transferData.status === 'confirmed' ? 'bg-gray-400 text-white' : 'bg-green-600 text-white hover:bg-green-700'}
                  sx={{ minWidth: '180px', fontWeight: 'bold', opacity: transferData.status === 'confirmed' ? 0.6 : 1 }}
                >
                  End & Import To POS
                </Button>
              )}
              {!isEditMode ? (
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => setIsEditMode(true)}
                  disabled={transferId && transferData.status !== 'pending'}
                >
                  Edit Transfer
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                  {canConfirmTransferDataPermission && transferId && transferData.status === 'pending' && isTransferReadyForConfirmation() ? (
                    <Button
                      variant="contained"
                      startIcon={<CheckCircle />}
                      onClick={() => handleOpenScanImport('confirm_transfer')}
                      className="bg-green-600 hover:bg-green-700"
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Double Check Serials'}
                    </Button>
                  ) : transferId && transferData.status === 'confirmed' ? (
                    <Button
                      variant="contained"
                      startIcon={<LocalShipping />}
                      onClick={handleProcessImport}
                      className="bg-purple-600 hover:bg-purple-700"
                      disabled={loading}
                      sx={{ minWidth: '250px' }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Click here to confirm & process import'}
                    </Button>
                  ) : (
                    canEditTransferProducts && (
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSaveTransfer}
                        className="bg-blue-600"
                      >
                        {transferId ? 'Update Transfer' : 'Create Transfer'}
                      </Button>
                    )
                  )}
                </>
              )}
            </Box>

          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead className="bg-gray-50">
                <TableRow>
                  <TableCell className="font-semibold">Product</TableCell>
                  <TableCell className="font-semibold">SKU</TableCell>
                  <TableCell className="font-semibold">Quantity</TableCell>
                  <TableCell className="font-semibold">Actual Qty</TableCell>
                  {(transferData.status === 'confirmed' || transferData.status === 'completed') && (
                    <TableCell className="font-semibold">Confirm Qty</TableCell>
                  )}
                  <TableCell className="font-semibold">Status</TableCell>
                  <TableCell className="font-semibold">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={(transferData.status === 'confirmed' || transferData.status === 'completed') ? 7 : 6} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No products added yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product, index) => {
                    const expectedQty = product.qty || 0;
                    const serialCount = (product.serials || []).length;
                    const isExactMatch = serialCount === expectedQty;
                    const isMissing = serialCount < expectedQty;
                    const isExcess = serialCount > expectedQty;

                    // Determine row background color
                    let rowBgColor = '';
                    if (isExactMatch) {
                      rowBgColor = '#d1fae5'; // Light green - exact match
                    } else if (isMissing) {
                      rowBgColor = '#fee2e2'; // Light red - missing items
                    } else if (isExcess) {
                      rowBgColor = '#fef3c7'; // Light yellow - excess items
                    }

                    return (
                      <TableRow
                        key={index}
                        ref={setProductRowRef(product.id || product.SKU)}
                        className={transferData.status === 'confirmed' || transferData.status === 'completed' ? '' : 'hover:bg-gray-50 cursor-pointer transition-colors duration-200'}
                        onClick={transferData.status === 'pending' ? () => handleViewProduct(product) : undefined}
                        sx={{
                          backgroundColor: rowBgColor,
                          '&:hover': transferData.status === 'pending' ? {
                            backgroundColor: rowBgColor ? rowBgColor : '#f5f5f5',
                            transform: 'scale(1.01)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            opacity: 0.9
                          } : {},
                          transition: 'background-color 0.5s ease',
                          cursor: transferData.status === 'pending' ? 'pointer' : 'default'
                        }}
                      >
                        <TableCell>
                          <Box className="flex items-center gap-3">
                            <Avatar
                              src={getProductImageSrc(product)}
                              sx={{ width: 40, height: 40 }}
                            >
                              <Inventory />
                            </Avatar>
                            <Typography variant="body2" className="font-medium">
                              {product.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell className="font-medium text-blue-600">
                          {product.SKU}
                        </TableCell>
                        <TableCell>{product.qty || 0}</TableCell>
                        <TableCell className="font-medium text-green-600">
                          {product.serials ? product.serials.length : 0}
                        </TableCell>
                        {(transferData.status === 'confirmed' || transferData.status === 'completed') && (
                          <TableCell className="font-medium text-purple-600">
                            {product.serials ? product.serials.length : 0}
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={
                              (product.qty || 0) === (product.serials ? product.serials.length : 0)
                                ? 'Match'
                                : 'NotMatch'
                            }
                            color={
                              (product.qty || 0) === (product.serials ? product.serials.length : 0)
                                ? 'success'
                                : 'error'
                            }
                            variant="filled"
                            size="small"
                            className="font-semibold"
                          />
                        </TableCell>
                        <TableCell>
                          {transferData.status === 'pending' ? (
                            <Box className="flex items-center gap-1">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewProduct(product);
                                }}
                                className="text-blue-600"
                                title="View Details"
                              >
                                <Visibility />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteProduct(product._id);
                                }}
                                className="text-red-600"
                                title="Delete Product"
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box className="flex items-center gap-1">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewProduct(product);
                                }}
                                className="text-blue-600"
                                title="View Details"
                              >
                                <Visibility />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Floating Action Button for Add Product */}
      {(transferData.status === 'pending' || !transferId) && (
        <Fab
          color="primary"
          aria-label="add product"
          onClick={handleAddNewProduct}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(45deg, #45a049 30%, #7cb342 90%)',
              color: 'white',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Product Detail Dialog */}
      {showProductDetail && (
        <ProductDetailDialog
          open={showProductDetail}
          onClose={handleCloseProductDetail}
          product={selectedProduct}
          onUpdateProduct={handleUpdateProduct}
          isEdit={isEditMode}
          existingProducts={products}
        />
      )}

      {/* Product Detail Modal */}
      {showProductDetailModal && selectedProduct && (
        <ProductDetailModal
          open={showProductDetailModal}
          onClose={handleCloseProductDetailModal}
          product={selectedProduct}
          onUpdateProduct={handleUpdateProductFromModal}
          allOrderSerials={getAllTransferSerials(selectedProduct._id)}
          orderStatus={transferData.status}
          orderId={transferId}
          allProducts={products}
          onRefreshData={loadTransferDetails}
          activeTags={activeTags}
          tagToSKU={tagToSKU}
          isSerialEditingLocked={isSerialEditingLocked}
        />
      )}

      {/* CSV Import Loading Modal */}
      <Dialog open={csvImportLoading} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box className="text-center py-6">
            <CircularProgress size={60} className="mb-4" />
            <Typography variant="h6" className="font-semibold mb-2">
              Importing CSV File
            </Typography>
            <Typography variant="body2" className="text-gray-600">
              {csvImportProgress || 'Processing...'}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* CSV Import Success/Error Messages */}
      {csvImportSuccess && (
        <Alert
          severity="success"
          className="fixed top-4 right-4 z-50 max-w-md"
          onClose={() => setCsvImportSuccess(null)}
        >
          {csvImportSuccess}
        </Alert>
      )}

      {csvImportError && (
        <Alert
          severity="error"
          className="fixed top-4 right-4 z-50 max-w-md"
          onClose={() => setCsvImportError(null)}
        >
          {csvImportError}
        </Alert>
      )}

      {/* Missing SKUs Modal */}
      <Dialog open={showMissingSKUsModal} onClose={() => { }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box className="flex items-center gap-2">
            <Box className="text-orange-600">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
              </svg>
            </Box>
            <Typography variant="h6" className="font-semibold text-orange-600">
              SKUs Not Found
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" className="mb-4">
            The following SKUs from your CSV file were not found in the system:
          </Typography>

          <Paper
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffecb5',
              mb: 3
            }}
          >
            <List>
              {missingSKUs.map((sku, index) => (
                <ListItem key={index} sx={{ borderBottom: index < missingSKUs.length - 1 ? '1px solid #ffecb5' : 'none' }}>
                  <ListItemText
                    primary={
                      <Box className="flex items-center gap-2">
                        <Box className="text-orange-600">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                          </svg>
                        </Box>
                        <Typography variant="body1" className="font-medium">
                          {sku}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{missingSKUs.length}</strong> SKU{missingSKUs.length !== 1 ? 's were' : ' was'} not found.
              {pendingValidProducts.length > 0 && (
                <> However, <strong>{pendingValidProducts.length}</strong> valid product{pendingValidProducts.length !== 1 ? 's were' : ' was'} found.</>
              )}
            </Typography>
          </Alert>

          <Typography variant="body2" className="text-gray-600">
            You can either skip these missing SKUs and continue importing the valid products, or clear all data and try again with a corrected CSV file.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClearMissingSkus}
            variant="outlined"
            color="error"
            startIcon={<Delete />}
          >
            Clear Data
          </Button>
          <Button
            onClick={handleSkipMissingSkus}
            variant="contained"
            color="warning"
            disabled={pendingValidProducts.length === 0}
          >
            Skip and Continue ({pendingValidProducts.length} valid)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Transfer Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="font-semibold text-red-600">
          Delete Transfer
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" className="mb-4">
            Are you sure you want to delete this transfer? This action cannot be undone.
          </Typography>
          <Typography variant="body2" className="text-gray-600">
            Transfer ID: <strong>{transferData.unique_key}</strong>
          </Typography>
          <Typography variant="body2" className="text-gray-600">
            Products: <strong>{products.length}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            variant="outlined"
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTransfer}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Flash message snackbar */}
      <Snackbar
        open={isVisible}
        autoHideDuration={5000}
        onClose={hideMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={hideMessage}
          severity={type}
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>

      {/* Scan to Confirm, Confirm & Proceed, and related dialogs removed - No longer needed */}

      {/* Scan To Confirm & Import Dialog */}
      <Dialog open={showScanImportDialog} onClose={handleCloseScanImport} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box className="flex justify-between items-center">
            <Box className="font-semibold text-purple-600">
              {scanDialogTitle}
            </Box>
            <IconButton onClick={handleCloseScanImport} size="small">
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" className="mb-4 text-gray-600">
            {scanDialogDescription}
          </Typography>

          {/* RFID Device Selection */}
          <Box className="mb-4 p-4 bg-gray-50 rounded">
            <Box className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
              <FormControl fullWidth>
                <InputLabel>Reader Device</InputLabel>
                <Select
                  value={selectedScanReaderDevice}
                  onChange={(e) => handleScanReaderDeviceChange(e.target.value)}
                  label="Reader Device"
                >
                  <MenuItem value="">
                    <em>Select a device</em>
                  </MenuItem>
                  {readerDevices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Handheld Device</InputLabel>
                <Select
                  value={selectedScanHandheldDevice}
                  onChange={(e) => handleScanHandheldDeviceChange(e.target.value)}
                  label="Handheld Device"
                >
                  <MenuItem value="">
                    <em>Select a device</em>
                  </MenuItem>
                  {handheldDevices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {hasScanActiveDevice && isConnected && (
                <Chip
                  label={
                    allProductsMatched
                      ? isConfirmTransferMode
                        ? 'Ready to confirm'
                        : 'Scan complete'
                      : 'Listening...'
                  }
                  color={allProductsMatched ? 'info' : 'success'}
                  icon={
                    allProductsMatched ? <CheckCircle /> : <CircularProgress size={16} color="inherit" />
                  }
                  sx={{ minWidth: '150px' }}
                />
              )}
            </Box>
            {selectedDeviceObjects.length === 0 && (
              <Alert severity="info" className="mt-2">
                No RFID devices available. Please go to Settings to select devices first.
              </Alert>
            )}
            {!isConnected && (
              <Alert severity="warning" className="mt-2">
                MQTT is not connected. Please check your connection settings.
              </Alert>
            )}
            {hasScanActiveDevice && isConnected && (
              <Typography variant="caption" color="text.secondary" className="mt-2 block">
                Auto-listening to{' '}
                {scanActiveDevices.map((deviceId) => getDeviceName(deviceId) || deviceId).join(', ')} - Scan
                RFID tags now
              </Typography>
            )}
          </Box>

          {/* Overall progress */}
          <Box className="mb-4">
            <Box className="flex items-center justify-between mb-1">
              <Typography variant="subtitle2" className="font-semibold text-gray-700">
                Overall progress
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                {totalScannedSerials} / {totalExpectedSerials} tags ({Math.round(overallProgressPercent)}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={overallProgressPercent}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead className="bg-gray-50">
                <TableRow>
                  <TableCell className="font-semibold">Product</TableCell>
                  <TableCell className="font-semibold">SKU</TableCell>
                  <TableCell className="font-semibold">Expected Qty</TableCell>
                  <TableCell className="font-semibold">Scanned Qty</TableCell>
                  <TableCell className="font-semibold">Status</TableCell>
                  <TableCell className="font-semibold">Tags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalProducts.map((product, index) => {
                  const productId = product.id || product.SKU;
                  const expectedQty = product.qty || 0;
                  const scannedTags = scannedProductTags[productId] || [];
                  const scannedQty = scannedTags.length;
                  const isMatched = scannedQty >= expectedQty;
                  const progressPercent = expectedQty > 0 ? (scannedQty / expectedQty) * 100 : 0;

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: isMatched ? '#f0fdf4' : 'transparent',
                        transition: 'background-color 0.3s ease',
                        borderLeft: isMatched ? '4px solid #22c55e' : '4px solid transparent'
                      }}
                    >
                      <TableCell>
                        <Box className="flex items-center gap-3">
                          <Avatar
                            src={getProductImageSrc(product)}
                            sx={{ width: 32, height: 32 }}
                          >
                            <Inventory />
                          </Avatar>
                          <Typography variant="body2" className="font-medium">
                            {product.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell className="font-medium text-blue-600">
                        {product.SKU}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {expectedQty}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box className="flex items-center gap-2">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              color: isMatched ? '#22c55e' : scannedQty > 0 ? '#f59e0b' : '#6b7280',
                              fontSize: '1rem'
                            }}
                          >
                            {scannedQty}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            / {expectedQty}
                          </Typography>
                          {scannedQty > 0 && !isMatched && (
                            <Typography variant="caption" color="warning.main">
                              ({Math.round(progressPercent)}%)
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isMatched ? '✓ Match' : 'Pending'}
                          color={isMatched ? 'success' : 'warning'}
                          variant="filled"
                          size="small"
                          sx={{
                            fontWeight: 'bold',
                            minWidth: '80px'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box className="flex flex-wrap gap-1">
                          {scannedTags.length > 0 ? (
                            scannedTags.map((tag, tagIndex) => (
                              <CopyableTag
                                key={tagIndex}
                                text={tag}
                                variant="chip"
                                size="small"
                                color="success"
                                chipVariant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No tags scanned
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {allProductsMatched && (
            <Alert severity="success" className="mt-4">
              {scanDialogSuccessMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseScanImport} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleScanDialogPrimaryAction}
            variant="contained"
            color="success"
            disabled={!allProductsMatched}
            startIcon={<CheckCircle />}
          >
            {isConfirmTransferMode ? 'Confirm Transfer Data' : 'Confirm Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP Confirmation Dialog */}
      <Dialog open={showOTPDialog} onClose={() => setShowOTPDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="font-semibold text-green-600">
          Enter OTP to Confirm Transfer
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" className="mb-4 text-gray-600">
            Please enter the 4-digit OTP code to confirm this transfer.
          </Typography>
          <TextField
            label="OTP Code"
            value={otpValue}
            onChange={(e) => setOtpValue(e.target.value)}
            fullWidth
            type="password"
            inputProps={{ maxLength: 4 }}
            placeholder="Enter 0000"
            autoFocus
            helperText="For testing: Enter 0000"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowOTPDialog(false);
            setOtpValue('');
          }} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleOTPSubmit}
            variant="contained"
            color="success"
            disabled={otpValue.length !== 4 || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Flash Dialog - All Products Matched */}
      <Dialog
        open={showMatchCompleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#10b981',
            color: 'white',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            animation: 'pulse 0.5s ease-in-out'
          }
        }}
      >
        <DialogContent>
          <Box className="flex flex-col items-center justify-center py-8 px-4">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                animation: 'bounce 1s ease-in-out'
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography variant="h5" className="font-bold text-center mb-2">
              All Products Matched!
            </Typography>
            <Typography variant="body1" className="text-center" sx={{ opacity: 0.95 }}>
              All products have been scanned successfully. You can now confirm the transfer.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Main Page Import Completion Dialog */}
      <Dialog
        open={showCompletionDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#10b981',
            color: 'white',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            animation: 'pulse 0.5s ease-in-out'
          }
        }}
      >
        <DialogContent>
          <Box className="flex flex-col items-center justify-center py-8 px-4">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                animation: 'bounce 1s ease-in-out'
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography variant="h5" className="font-bold text-center mb-2">
              Serial import completed
            </Typography>
            <Typography variant="body1" className="text-center" sx={{ opacity: 0.95 }}>
              {autoSaving ? 'Auto-saving data...' : 'All products have been scanned successfully'}
            </Typography>
            {autoSaving && (
              <Box sx={{ mt: 2 }}>
                <CircularProgress size={32} sx={{ color: 'white' }} />
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* End & Import To POS - Confirmation Modal */}
      <Dialog
        open={showEndImportDialog}
        onClose={() => setShowEndImportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box className="flex items-center gap-2">
            <LocalShipping className="text-green-600" />
            <Typography variant="h6" className="font-bold">
              Final step: Scan all goods again to confirm the order.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box className="space-y-4">
            {/* Device Info - Auto-detected */}
            {hasScanActiveDevice ? (
              <Alert severity="info" className="mb-4">
                <Typography variant="body2">
                  <strong>Auto-listening from:</strong>{' '}
                  {scanActiveDevices
                    .map((deviceId) => getDeviceName(deviceId) || deviceId)
                    .join(', ')}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Scan products with RFID to confirm. Each tag will be matched once.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="warning" className="mb-4">
                <Typography variant="body2">
                  <strong>No RFID device selected.</strong> Please select a device in Step 2 & 3 section above before continuing.
                </Typography>
              </Alert>
            )}

            <Box className="mb-4 p-4 bg-gray-50 rounded">
              <Typography variant="subtitle2" className="font-semibold text-gray-700 mb-2">
                Select devices for this verification
              </Typography>
              <Box className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                <FormControl fullWidth>
                  <InputLabel>Reader Device</InputLabel>
                  <Select
                    value={selectedScanReaderDevice}
                    onChange={(e) => handleScanReaderDeviceChange(e.target.value)}
                    label="Reader Device"
                  >
                    <MenuItem value="">
                      <em>Select a device</em>
                    </MenuItem>
                    {readerDevices.map((device) => (
                      <MenuItem key={device.id} value={device.id}>
                        {device.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Handheld Device</InputLabel>
                  <Select
                    value={selectedScanHandheldDevice}
                    onChange={(e) => handleScanHandheldDeviceChange(e.target.value)}
                    label="Handheld Device"
                  >
                    <MenuItem value="">
                      <em>Select a device</em>
                    </MenuItem>
                    {handheldDevices.map((device) => (
                      <MenuItem key={device.id} value={device.id}>
                        {device.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {hasScanActiveDevice && isConnected && (
                  <Chip
                    label="Listening..."
                    color="success"
                    icon={<CircularProgress size={16} color="inherit" />}
                    sx={{ minWidth: '150px' }}
                  />
                )}
              </Box>
              {!isConnected && (
                <Alert severity="warning" className="mt-2">
                  MQTT is not connected. Please check your connection settings.
                </Alert>
              )}
              {hasScanActiveDevice && isConnected && (
                <Typography variant="caption" color="text.secondary" className="mt-2 block">
                  Active devices:{' '}
                  {scanActiveDevices.map((deviceId) => getDeviceName(deviceId) || deviceId).join(', ')}
                </Typography>
              )}
            </Box>

            {/* Products List */}
            <Box>
              <Typography variant="subtitle1" className="font-semibold mb-2">
                Products to Confirm:
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="center">Total Serials</TableCell>
                      <TableCell align="center">Confirmed</TableCell>
                      <TableCell align="center">Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {confirmingProducts.map((product) => {
                      const totalSerials = product.serials?.length || 0;
                      const confirmedSerials = product.serialsConfirm?.length || 0;
                      const progress = totalSerials > 0 ? (confirmedSerials / totalSerials) * 100 : 0;

                      return (
                        <TableRow key={product._id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" className="font-semibold">
                                {product.name}
                              </Typography>
                              <Typography variant="caption" className="text-gray-500">
                                {product.SKU}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{totalSerials}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={confirmedSerials}
                              color={confirmedSerials === totalSerials ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box className="flex items-center gap-2">
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 8,
                                  backgroundColor: '#e0e0e0',
                                  borderRadius: 1,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${progress}%`,
                                    height: '100%',
                                    backgroundColor: progress === 100 ? '#4caf50' : '#2196f3',
                                    transition: 'width 0.3s ease'
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" className="min-w-[40px]">
                                {Math.round(progress)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowEndImportDialog(false);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={completeEndImportToPOS}
            variant="contained"
            color="success"
            disabled={loading || !hasScanActiveDevice || !confirmingProducts.every(p =>
              (p.serialsConfirm?.length || 0) === (p.serials?.length || 0)
            )}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {loading ? 'Processing...' : 'Complete Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag List Modal */}
      <Dialog
        open={showTagListModal}
        onClose={() => setShowTagListModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box className="flex justify-between items-center">
            <Typography variant="h6" className="font-bold">
              Tag List ({uniqueMessagesCount})
            </Typography>
            <IconButton onClick={() => setShowTagListModal(false)} size="small">
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, maxHeight: '60vh', overflowY: 'auto' }}>
            {(() => {
              const uniqueMessagesSet = new Set(messageHashMapRef.current.values());
              const uniqueTagsArray = Array.from(uniqueMessagesSet).sort();

              if (uniqueTagsArray.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary" className="text-center py-4">
                    No tags available
                  </Typography>
                );
              }

              return (
                <Box className="flex flex-wrap gap-2">
                  {uniqueTagsArray.map((tagId, index) => {
                    const sku = mainPageTagToSKU[tagId];
                    const displayText = sku ? `${tagId} (${sku})` : tagId;

                    return (
                      <Box
                        key={index}
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(tagId);
                            showSuccess('Tag ID copied to clipboard');
                          } catch (err) {
                            showError('Failed to copy tag ID');
                          }
                        }}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          padding: '4px 12px',
                          borderRadius: 2,
                          backgroundColor: sku ? '#dbeafe' : '#f3f4f6',
                          border: '1px solid',
                          borderColor: sku ? '#93c5fd' : '#d1d5db',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: sku ? '#bfdbfe' : '#e5e7eb',
                            transform: 'scale(1.05)',
                            boxShadow: 2
                          }
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 'medium',
                            color: sku ? '#1e40af' : '#374151'
                          }}
                        >
                          {displayText}
                        </Typography>
                        <ContentCopy sx={{ fontSize: 14, color: sku ? '#3b82f6' : '#6b7280' }} />
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTagListModal(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* All Serials Confirmed Success Modal */}
      <Dialog
        open={showAllConfirmedDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#10b981',
            color: 'white',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            animation: 'pulse 0.5s ease-in-out'
          }
        }}
      >
        <DialogContent>
          <Box className="flex flex-col items-center justify-center py-8 px-4">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                animation: 'bounce 1s ease-in-out'
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography variant="h5" className="font-bold text-center mb-2">
              All Products Confirmed!
            </Typography>
            <Typography variant="body1" className="text-center" sx={{ opacity: 0.95 }}>
              You have successfully confirmed all product serials. Ready to complete the import to POS.
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                setShowAllConfirmedDialog(false);
                completeEndImportToPOS();
              }}
              sx={{
                mt: 3,
                backgroundColor: 'white',
                color: '#10b981',
                '&:hover': {
                  backgroundColor: '#f0fdf4'
                }
              }}
            >
              Complete Import Now
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default InventoryTransferCreatePage;
